import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { clearAuth, isSuperAdmin, isOwner, isOpsPf, isFinance, isFinanceOnly, isSupport, isSupportOnly, getCurrentUser } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { Shield, ShieldCheck, Building2, Users, ArrowLeft, ArrowUpDown, LogOut, Plus, Edit, Trash2, Power, UserPlus, Mail, Bot, Save, CheckCircle, AlertCircle, AlertTriangle, RefreshCw, FileText, Clock, XCircle, Download, RotateCcw, FolderOpen, FileSearch, Landmark, Star, ClipboardList, CreditCard, BarChart3, Cpu, Check, MoreHorizontal, Globe, ChevronDown, Network, Copy, Settings, Zap, Database, Key, Search, Upload, X, Eye, Smartphone, Activity, Gauge, Loader2, Sparkles, Heart, ListChecks, LayoutDashboard, FileSignature, Wallet, Boxes, LifeBuoy, Phone, Receipt, Radio, Leaf, DollarSign, Scale, History } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { aiAgentApi } from '../lib/aiAgentApi';
import { SmsPhoneCard } from '../components/SmsPhoneCard';
import BillStreamReport from './BillStreamReport';
import InvoicesPage from './invoices/InvoicesPage';
import AccountBillReport from './reports/AccountBillReport';
import MySqlBrowser from './MySqlBrowser';
import DocumentationPage from './superadmin/DocumentationPage';
import CrossCustomerReportPage from './superadmin/CrossCustomerReportPage';
import WorkflowConfigTab from './superadmin/WorkflowConfigTab';
import RegressionDashboard from './superadmin/RegressionDashboard';
import RegistrationConfigTab from '../components/superadmin/RegistrationConfigTab';
import OAuthConfigTab from '../components/superadmin/OAuthConfigTab';
import SubscriptionPricingTab from '../components/superadmin/SubscriptionPricingTab';
import BillingInvoicesTab from '../components/superadmin/BillingInvoicesTab';
import AddCreditsTab from '../components/superadmin/AddCreditsTab';
import CreditTopupReportTab from '../components/superadmin/CreditTopupReportTab';
import CrmSyncTab from '../components/superadmin/CrmSyncTab';
import BackendCrmTab from '../components/superadmin/BackendCrmTab';
import EmissionFactorsTab from '../components/superadmin/EmissionFactorsTab';
import FinanceReportTab from '../components/superadmin/FinanceReportTab';
import FinanceRefundsTab from '../components/superadmin/FinanceRefundsTab';
import SupportRequestsTab from '../components/superadmin/SupportRequestsTab';
import StripeConfigTab from '../components/superadmin/StripeConfigTab';
import BillingHistoryTab from '../components/superadmin/BillingHistoryTab';
const DbBrowser = MySqlBrowser;
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';

function ExportReportsTab() {
  const [downloading, setDownloading] = useState(false);

  const handleExportMigration = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/v2/export/migration-report`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = res.headers.get('content-disposition');
      const filename = disposition ? disposition.split('filename=')[1] : 'migration_report.csv';
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Migration report downloaded');
    } catch (err) {
      toast.error(err.message || 'Failed to generate report');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="export-reports-tab">
      <div className="flex items-center gap-3 mb-6">
        <Download className="h-6 w-6 text-primary" />
        <div>
          <h2 className="text-xl font-bold">Export Reports</h2>
          <p className="text-sm text-muted-foreground">Generate and download platform-wide data exports</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Migration Data Export Card */}
        <div className="border border-border rounded-lg p-5 bg-card hover:border-primary/40 transition-colors" data-testid="migration-export-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex items-center justify-center h-10 w-10 rounded-md bg-blue-500/15 text-blue-600 flex-shrink-0">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">Migration Data Report</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Mapped migration data to Payment File records of all customers in one export
              </p>
            </div>
          </div>
          <div className="space-y-2 text-xs text-muted-foreground mb-4">
            <p>Includes all columns from Paid PF invoices table:</p>
            <ul className="list-disc list-inside space-y-0.5 ml-1">
              <li>Customer Name (Org) + Batch Name + PF Status</li>
              <li>Invoice details, amounts, payment data</li>
              <li>Variance + Service Charge columns</li>
              <li>Excel row mapping + Excel status/amount</li>
              <li>Migration comments &amp; discrepancies</li>
            </ul>
            <p className="text-[10px] mt-2 font-medium text-foreground/70">
              Scope: Approved + Paid PFs across all organizations
            </p>
          </div>
          <button
            onClick={handleExportMigration}
            disabled={downloading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="export-migration-btn"
          >
            {downloading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating Report...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Export CSV
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// SA Payments Dashboard Tab — renders inside SuperAdmin page
function SAPaymentsDashboardTab() {
  const PaymentsDashboard = require('./payments/PaymentsDashboard').default;
  return <PaymentsDashboard isGlobal={true} noLayout={true} />;
}

// SA Payments Workspace Tab — renders inside SuperAdmin page
function SAPaymentsWorkspaceTab() {
  const PaymentsBillsWorkspace = require('./payments/PaymentsBillsWorkspace').default;
  return <PaymentsBillsWorkspace isGlobal={true} noLayout={true} />;
}

// SA Global Credit/Debit Tab — renders inside SuperAdmin page
function SAGlobalCreditDebitTab() {
  const GlobalCreditDebitPage = require('./payments/GlobalCreditDebitPage').default;
  return <GlobalCreditDebitPage noLayout={true} />;
}

// SA Upload Credit File Tab — renders inside SuperAdmin page
function SAUploadCreditFileTab() {
  const UploadCreditFilePage = require('./payments/UploadCreditFilePage').default;
  return <UploadCreditFilePage noLayout={true} />;
}

// SA Process Refunds Tab — renders the Refund Pending Review page inside SuperAdmin
function SAProcessRefundsTab({ accounts }) {
  const RefundPendingPage = require('./cockpit/RefundPendingPage').default;
  return <RefundPendingPage accounts={accounts} />;
}

export const SuperAdmin = () => {
  const navigate = useNavigate();
  const location = (() => { try { return require('react-router-dom').useLocation(); } catch { return { search: '' }; } })();
  const initialTab = (() => {
    try {
      const sp = new URLSearchParams(location.search || '');
      return sp.get('tab') || 'dashboard';
    } catch { return 'dashboard'; }
  })();
  const [activeTab, setActiveTab] = useState(() => {
    // Ops PF users land on Payments Dashboard by default
    if (isOpsPf() && !isSuperAdmin()) {
      try {
        const sp = new URLSearchParams(location.search || '');
        const t = sp.get('tab');
        // Allow only Super Admin > Payments tabs for Ops PF
        const allowed = new Set(['sa_payments_dashboard', 'sa_payments_workspace', 'sa_global_credit_debit', 'sa_upload_credit_file', 'sa_process_refunds']);
        return t && allowed.has(t) ? t : 'sa_payments_dashboard';
      } catch { return 'sa_payments_dashboard'; }
    }
    // Finance-only users land on the Finance Credit/Debit report
    if (isFinanceOnly()) {
      try {
        const sp = new URLSearchParams(location.search || '');
        const t = sp.get('tab');
        const allowed = new Set(['sa_finance_report', 'sa_finance_refunds', 'finance-invoices', 'sub-pricing', 'add-credits', 'topup-report', 'crm-sync', 'registration', 'billing-history']);
        return t && allowed.has(t) ? t : 'sa_finance_report';
      } catch { return 'sa_finance_report'; }
    }
    // Support-Team-only users are limited to Dashboard + Support Requests
    if (isSupportOnly()) {
      try {
        const sp = new URLSearchParams(location.search || '');
        const t = sp.get('tab');
        const allowed = new Set(['dashboard', 'support-requests']);
        return t && allowed.has(t) ? t : 'support-requests';
      } catch { return 'support-requests'; }
    }
    return initialTab;
  });
  const [accounts, setAccounts] = useState([]);
  // Search + Partner filter for Organisations tab
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const [orgPartnerFilter, setOrgPartnerFilter] = useState('all'); // 'all' | 'none' | <partner_id>
  const [partnerFilterOpen, setPartnerFilterOpen] = useState(false);

  // Derived: filtered account list based on search + partner selection
  const filteredAccounts = useMemo(() => {
    const q = orgSearchQuery.trim().toLowerCase();
    return accounts.filter((a) => {
      // Partner filter
      if (orgPartnerFilter === 'none') {
        if (a.bp_partner_id) return false;
      } else if (orgPartnerFilter !== 'all') {
        if (String(a.bp_partner_id || '') !== String(orgPartnerFilter)) return false;
      }
      // Text search across org name + account no
      if (!q) return true;
      return (
        (a.organisation_name || a.company_name || '').toLowerCase().includes(q) ||
        (a.account_no || '').toLowerCase().includes(q)
      );
    });
  }, [accounts, orgSearchQuery, orgPartnerFilter]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountMembers, setAccountMembers] = useState([]);
  
  // Modal states
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [showEditAccount, setShowEditAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Form states
  const [accountForm, setAccountForm] = useState({
    organisation_name: '',
    subscription_plan: 'free',
    bp_org_id: null,
    bp_partner_id: null,
    subscription_start_date: '',
    subscription_end_date: '',
  });
  const [editForm, setEditForm] = useState({
    organisation_name: '',
    account_no: '',
    subscription_plan: 'free',
    bp_org_id: null,
    bp_partner_id: null,
    subscription_start_date: '',
    subscription_end_date: '',
    subscription_status: 'open',
    missing_bill_start_date: '',
    status: 'active',
    mask_customer_names: false,
    mask_service_addresses: false,
    enabled_apps: {
      portal_collection: true,
      bill_parsing: true,
      ubm: false,
      cockpit: false,
      energy_star: false,
      sustainability: false,
      opt: false,
      payments: false,
      analytics: false,
      ai_operations: false,
    },
  });
  const [editDialogTab, setEditDialogTab] = useState('details');
  const [nachaOriginatorConfig, setNachaOriginatorConfig] = useState({
    company_name: '',
    company_tax_id: '',
    odfi_routing_number: '',
    odfi_account_number: '',
    odfi_account_name: '',
    odfi_bank_name: '',
    immediate_destination_routing: '',
    immediate_destination_name: '',
  });
  const [savingNachaConfig, setSavingNachaConfig] = useState(false);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'viewer',
  });
  const [createUserForm, setCreateUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'viewer',
    is_super_admin: false,
    is_super_pf_admin: false,
    is_ops_admin: false,
    is_ops_editor: false,
    is_ops_pf: false,
    is_finance: false,
    is_support: false,
    assigned_accounts: [], // Array of account IDs
    default_page: '/bills/dashboard',
  });
  const [editUserForm, setEditUserForm] = useState({
    name: '',
    is_super_admin: false,
    is_super_pf_admin: false,
    is_ops_admin: false,
    is_ops_editor: false,
    is_ops_pf: false,
    is_finance: false,
    is_support: false,
    default_role: 'viewer',
    assigned_accounts: [],
    account_roles: [], // [{"account_id": "x", "role": "admin"}]
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(false);
  
  // Email Config state
  const [emailConfig, setEmailConfig] = useState({
    provider: 'none',
    api_key: '',
    webhook_signing_key: '',
    domain: 'inbound.billportal.io',
    webhook_url: '',
    is_configured: false,
  });
  const [savingEmail, setSavingEmail] = useState(false);

  // Support Email state
  const [supportConfig, setSupportConfig] = useState({ support_email: 'support@billportal.io', cc_user: true });
  const [savingSupport, setSavingSupport] = useState(false);
  const [supportRequests, setSupportRequests] = useState([]);
  const [loadingSupportRequests, setLoadingSupportRequests] = useState(false);

  // AI Config state
  const [aiConfig, setAiConfig] = useState({
    provider: 'emergent',
    api_key: '',
    model_name: 'gpt-5.2',
  });
  const [savingAi, setSavingAi] = useState(false);

  // Dashboard state
  const [dashboardStats, setDashboardStats] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardTimeRange, setDashboardTimeRange] = useState('90');
  const [supplierAnalytics, setSupplierAnalytics] = useState(null);
  const [supplierAnalyticsLoading, setSupplierAnalyticsLoading] = useState(false);
  const [supplierPartnerId, setSupplierPartnerId] = useState('all');
  const [showSmsSheet, setShowSmsSheet] = useState(false);

  // Lazy loading refs for scroll-triggered data fetching
  const dashboardStatsRef = useRef(null);
  const dashboardStatsLoaded = useRef(false);

  // BP MySQL mapping data (partners & customers/orgs)
  const [bpPartners, setBpPartners] = useState([]);
  const [bpCustomers, setBpCustomers] = useState([]);

  // (Job Queues moved to Cockpit)
  // Bulk reprocess state (used in organisations tab)
  const [bulkReprocessLoading, setBulkReprocessLoading] = useState(false);
  const [bulkReprocessOrgId, setBulkReprocessOrgId] = useState(null);

  // All Users state
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersTotalCount, setUsersTotalCount] = useState(0);
  const [usersFilters, setUsersFilters] = useState({
    role: 'all',
    account_id: 'all',
    status: 'all',
    search: '',
  });

  // Outbound IP Config state
  const [outboundIpData, setOutboundIpData] = useState(null);
  const [outboundIpLoading, setOutboundIpLoading] = useState(false);

  // Migration Key state
  const [migrationKey, setMigrationKey] = useState(null);
  const [migrationKeyLoading, setMigrationKeyLoading] = useState(false);

  // System Health state (for dashboard)
  const [systemHealth, setSystemHealth] = useState(null);
  const [systemHealthLoading, setSystemHealthLoading] = useState(false);
  const [showPgErrorDialog, setShowPgErrorDialog] = useState(false);

  // Vendor Registry state
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showDeleteVendor, setShowDeleteVendor] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [reviewQueueLoading, setReviewQueueLoading] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeVendor, setMergeVendor] = useState(null);
  const [vendorForm, setVendorForm] = useState({
    vendor_name: '',
    vendor_code: '',
    email: '',
    support_phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    website: '',
    portal_url: '',
    commodities_served: [],
    payment_methods: {
      ach: { enabled: false },
      check: { enabled: true },
      credit_card: { enabled: false },
      wire: { enabled: false },
      account_pay: { enabled: false },
      auto_pay: { enabled: false },
    },
  });

  // Payment Files Engine state
  const [pfeFiles, setPfeFiles] = useState([]);
  const [pfeFilesLoading, setPfeFilesLoading] = useState(false);
  const [pfeStats, setPfeStats] = useState({ total: 0, pending_review: 0, assigned: 0, published: 0 });
  const [pfeCustomerConfigs, setPfeCustomerConfigs] = useState([]);
  const [pfePulling, setPfePulling] = useState(false);
  const [pfeStatusFilter, setPfeStatusFilter] = useState('all');
  const [pfeTab, setPfeTab] = useState('unassigned');
  const [pfeOrgFilter, setPfeOrgFilter] = useState('all');
  const [pfeSelectedFiles, setPfeSelectedFiles] = useState(new Set());
  const [pfeBulkPublishing, setPfeBulkPublishing] = useState(false);
  const [pfeUseOriginalAmounts, setPfeUseOriginalAmounts] = useState(false);
  const [showAddCustomerConfig, setShowAddCustomerConfig] = useState(false);
  const [newCustomerConfig, setNewCustomerConfig] = useState({ customer_id: '', customer_name: '', auto_assign_account_id: '', auto_assign_account_name: '' });
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assigningFile, setAssigningFile] = useState(null);
  const [pfePreviewFiles, setPfePreviewFiles] = useState([]);
  const [pfePreviewLoading, setPfePreviewLoading] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewCustomerId, setPreviewCustomerId] = useState('');
  const [duplicateAction, setDuplicateAction] = useState('skip');
  const [pfePullingSingle, setPfePullingSingle] = useState(null);

  // UBM Sync state
  const [ubmSyncStatus, setUbmSyncStatus] = useState(null);
  const [ubmSyncLoading, setUbmSyncLoading] = useState(false);
  const [ubmSyncing, setUbmSyncing] = useState(false);
  const [ubmSyncHistory, setUbmSyncHistory] = useState([]);
  const [ubmCustomerSyncing, setUbmCustomerSyncing] = useState(null);
  
  // Sync Health Dashboard state
  const [syncProgress, setSyncProgress] = useState(null);
  const [syncErrors, setSyncErrors] = useState([]);
  const [syncProgressHistory, setSyncProgressHistory] = useState([]);
  
  // Customer Report state
  const [customerReport, setCustomerReport] = useState([]);
  const [customerReportLoading, setCustomerReportLoading] = useState(false);
  
  // Sync History customer filter
  const [syncHistoryCustomerFilter, setSyncHistoryCustomerFilter] = useState(null);

  useEffect(() => {
    if (!isSuperAdmin() && !isOpsPf() && !isFinance() && !isSupport()) {
      navigate('/dashboard');
      return;
    }
    // Finance-only and Support-only users get a scoped console and cannot access
    // these SA-only endpoints — skip them to avoid 403 noise and misleading toasts.
    const financeScoped = isFinanceOnly();
    const supportScoped = isSupportOnly();
    if (!financeScoped && !supportScoped) {
      fetchAccounts();
    }
    if (!isOpsPf() && !financeScoped && !supportScoped) {
      fetchGlobalConfig();
      fetchSystemHealth();
    }
    // Load BP MySQL partners & customers for org mapping (skip for Ops PF / Finance / Support)
    if (!isOpsPf() && !financeScoped && !supportScoped) {
      api.get('/payment-verification/v2/reports/partners')
        .then(({ data }) => setBpPartners(data.partners || []))
        .catch(() => {});
      api.get('/payment-verification/v2/reports/customers')
        .then(({ data }) => setBpCustomers(data.customers || []))
        .catch(() => {});
    }
  }, [navigate]);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      // Supplier analytics loads immediately (it's at the top of dashboard)
      fetchSupplierAnalytics();
      // Dashboard stats loaded lazily via IntersectionObserver below
      dashboardStatsLoaded.current = false;
    }
    // Job Queues moved to Cockpit
    if (activeTab === 'all-users') {
      fetchAllUsers();
    }
    if (activeTab === 'vendor-registry') {
      fetchVendors();
      fetchReviewQueue();
    }
    if (activeTab === 'outbound-ip') {
      fetchOutboundIp();
    }
    if (activeTab === 'payment-files') {
      fetchPfeFiles();
      fetchPfeStats();
      fetchPfeCustomerConfigs();
    }
    if (activeTab === 'ubm-sync') {
      // UBM Sync now handled by UbmSyncDashboard component
    }
    if (activeTab === 'report-customer') {
      fetchCustomerReport();
    }
    if (activeTab === 'support-email') {
      fetchSupportRequests();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, usersFilters, dashboardTimeRange]);

  // Lazy load dashboard stats when section scrolls into view
  useEffect(() => {
    if (activeTab !== 'dashboard') return;
    const node = dashboardStatsRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !dashboardStatsLoaded.current) {
          dashboardStatsLoaded.current = true;
          fetchDashboardStats();
          fetchSystemHealth();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchAccounts = async () => {
    try {
      const { data } = await api.get('/super-admin/accounts');
      setAccounts(data);
    } catch (error) {
      toast.error('Failed to load accounts');
    }
  };

  const fetchOutboundIp = async () => {
    setOutboundIpLoading(true);
    try {
      const { data } = await api.get('/super-admin/outbound-ip');
      setOutboundIpData(data);
    } catch (error) {
      toast.error('Failed to check outbound IP');
    } finally {
      setOutboundIpLoading(false);
    }
  };

  const fetchSystemHealth = async () => {
    setSystemHealthLoading(true);
    try {
      const { data } = await api.get('/super-admin/system-health');
      setSystemHealth(data);
    } catch (error) {
      console.error('Failed to check system health:', error);
    } finally {
      setSystemHealthLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Payment Files Engine functions
  const fetchPfeFiles = async () => {
    setPfeFilesLoading(true);
    try {
      // Always fetch all files - we filter client-side by tab
      const { data } = await api.get('/payment-files-engine/files');
      setPfeFiles(data.files || []);
    } catch (error) {
      toast.error('Failed to load payment files');
    } finally {
      setPfeFilesLoading(false);
    }
  };

  const fetchPfeStats = async () => {
    try {
      const { data } = await api.get('/payment-files-engine/stats');
      setPfeStats(data.stats || { total: 0, pending_review: 0, assigned: 0, published: 0 });
    } catch (error) {
      console.error('Failed to load PFE stats:', error);
    }
  };

  const fetchPfeCustomerConfigs = async () => {
    try {
      const { data } = await api.get('/payment-files-engine/customer-configs');
      setPfeCustomerConfigs(data.configs || []);
    } catch (error) {
      console.error('Failed to load customer configs:', error);
    }
  };

  const handlePullFromEngine = async () => {
    if (pfeCustomerConfigs.length === 0) {
      toast.error('No customer configurations found. Add a customer ID first.');
      return;
    }
    setPfePulling(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const customerIds = pfeCustomerConfigs.map(c => c.customer_id).join(',');
      const { data } = await api.post(
        `/payment-files-engine/pull?user_id=${user.id}&user_name=${encodeURIComponent(user.name)}&customer_ids=${customerIds}&duplicate_action=${duplicateAction}`
      );
      const result = data.result;
      let msg = `Pulled ${result.new_files} new files.`;
      if (result.auto_assigned > 0) msg += ` ${result.auto_assigned} auto-assigned.`;
      if (result.duplicates_skipped > 0) msg += ` Skipped ${result.duplicates_skipped} duplicates.`;
      toast.success(msg);
      fetchPfeFiles();
      fetchPfeStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pull payment files');
    } finally {
      setPfePulling(false);
    }
  };

  const handlePreviewEngineFiles = async () => {
    if (!previewCustomerId.trim()) {
      toast.error('Please enter a customer ID');
      return;
    }
    setPfePreviewLoading(true);
    try {
      const { data } = await api.get(`/payment-files-engine/preview?customer_id=${previewCustomerId}`);
      setPfePreviewFiles(data.files || []);
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview files');
    } finally {
      setPfePreviewLoading(false);
    }
  };

  const handlePullSingleFile = async (file) => {
    setPfePullingSingle(file.paymentFileId);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const { data } = await api.post(
        `/payment-files-engine/pull-single?customer_id=${file.customerId}&payment_file_id=${file.paymentFileId}&user_id=${user.id}&user_name=${encodeURIComponent(user.name)}&duplicate_action=${duplicateAction}`
      );
      if (data.result?.new_files > 0) {
        toast.success(`Pulled: ${file.batchName}`);
        // Mark as exists in preview list
        setPfePreviewFiles(prev => prev.map(f => 
          f.paymentFileId === file.paymentFileId ? { ...f, exists: true } : f
        ));
        // Refresh stats and file list
        fetchPfeStats();
        fetchPfeFiles();
      } else if (data.result?.duplicates_skipped > 0) {
        toast.info('File already exists (skipped)');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pull file');
    } finally {
      setPfePullingSingle(null);
    }
  };

  const handleAddCustomerConfig = async () => {
    if (!newCustomerConfig.customer_id.trim() || !newCustomerConfig.customer_name.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      let url = `/payment-files-engine/customer-configs?customer_id=${newCustomerConfig.customer_id}&customer_name=${encodeURIComponent(newCustomerConfig.customer_name)}&user_id=${user.id}`;
      if (newCustomerConfig.auto_assign_account_id) {
        url += `&account_id=${newCustomerConfig.auto_assign_account_id}&account_name=${encodeURIComponent(newCustomerConfig.auto_assign_account_name)}`;
      }
      await api.post(url);
      toast.success('Customer configuration added');
      setShowAddCustomerConfig(false);
      setNewCustomerConfig({ customer_id: '', customer_name: '', auto_assign_account_id: '', auto_assign_account_name: '' });
      fetchPfeCustomerConfigs();
    } catch (error) {
      toast.error('Failed to add customer configuration');
    }
  };

  const handleDeleteCustomerConfig = async (customerId) => {
    if (!confirm('Are you sure you want to delete this customer configuration?')) return;
    try {
      await api.delete(`/payment-files-engine/customer-configs/${customerId}`);
      toast.success('Customer configuration deleted');
      fetchPfeCustomerConfigs();
    } catch (error) {
      toast.error('Failed to delete customer configuration');
    }
  };

  const handleAssignFile = async () => {
    if (!assigningFile || !assigningFile.selectedAccountId) {
      toast.error('Please select an organization');
      return;
    }
    const account = accounts.find(a => a.id === assigningFile.selectedAccountId);
    if (!account) {
      toast.error('Account not found');
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await api.post(
        `/payment-files-engine/files/${assigningFile.external_file_id}/assign?account_id=${account.id}&account_name=${encodeURIComponent(account.organisation_name)}&user_id=${user.id}`
      );
      toast.success('File assigned to organization');
      setShowAssignDialog(false);
      setAssigningFile(null);
      fetchPfeFiles();
      fetchPfeStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign file');
    }
  };

  const handlePublishFile = async (file) => {
    if (!file.assigned_account_id) {
      toast.error('File must be assigned to an organization first');
      return;
    }
    if (!confirm(`Publish this file to ${file.assigned_account_name}? This will make it visible at the org level.`)) return;
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const { data } = await api.post(
        `/payment-files-engine/files/${file.external_file_id}/publish?user_id=${user.id}&user_name=${encodeURIComponent(user.name)}&use_original_amounts=${pfeUseOriginalAmounts}`
      );
      toast.success(`File published! Batch ID: ${data.batch_id}`);
      fetchPfeFiles();
      fetchPfeStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish file');
    }
  };

  const handleBulkPublish = async () => {
    const fileIds = Array.from(pfeSelectedFiles);
    if (fileIds.length === 0) {
      toast.error('No files selected');
      return;
    }
    if (!confirm(`Publish ${fileIds.length} file(s)? This will make them visible at their assigned org level.`)) return;
    setPfeBulkPublishing(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const { data } = await api.post(
        `/payment-files-engine/bulk-publish?user_id=${user.id}&user_name=${encodeURIComponent(user.name)}&use_original_amounts=${pfeUseOriginalAmounts}`,
        { file_ids: fileIds }
      );
      if (data.succeeded > 0) toast.success(`${data.succeeded} file(s) published successfully`);
      if (data.failed > 0) toast.error(`${data.failed} file(s) failed to publish`);
      setPfeSelectedFiles(new Set());
      fetchPfeFiles();
      fetchPfeStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk publish failed');
    } finally {
      setPfeBulkPublishing(false);
    }
  };

  const toggleFileSelection = (fileId) => {
    setPfeSelectedFiles(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  const toggleSelectAll = (files) => {
    const selectableFiles = files.filter(f => f.local_status === 'assigned');
    if (pfeSelectedFiles.size === selectableFiles.length && selectableFiles.length > 0) {
      setPfeSelectedFiles(new Set());
    } else {
      setPfeSelectedFiles(new Set(selectableFiles.map(f => f.external_file_id)));
    }
  };

  const handleDeletePfeFile = async (externalFileId) => {
    if (!confirm('Are you sure you want to delete this file?')) return;
    try {
      await api.delete(`/payment-files-engine/files/${externalFileId}`);
      toast.success('File deleted');
      fetchPfeFiles();
      fetchPfeStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete file');
    }
  };

  const handleDownloadEngineCsv = async (file) => {
    try {
      toast.info('Fetching CSV download link...');
      const { data } = await api.get(`/payment-files-engine/files/${file.external_file_id}/csv-url`);
      if (data.url) {
        window.open(data.url, '_blank');
        toast.success('Download started');
      } else {
        toast.error('No CSV URL available');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get CSV download link');
    }
  };

  const handleUnpublishFile = async (file) => {
    if (!confirm(`Unpublish "${file.batch_name}"? This will delete the associated batch and invoices from the org. The file can then be republished.`)) return;
    try {
      await api.post(`/payment-files-engine/files/${file.external_file_id}/unpublish`);
      toast.success('File unpublished. You can now republish it.');
      fetchPfeFiles();
      fetchPfeStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unpublish file');
    }
  };

  const fetchDashboardStats = async () => {
    setDashboardLoading(true);
    try {
      const { data } = await api.get(`/super-admin/dashboard/stats?days=${dashboardTimeRange}`);
      setDashboardStats(data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setDashboardLoading(false);
    }
  };

  const fetchSupplierAnalytics = async (pid) => {
    setSupplierAnalyticsLoading(true);
    try {
      const filterPid = pid !== undefined ? pid : supplierPartnerId;
      const params = filterPid && filterPid !== 'all' ? `?partner_id=${filterPid}` : '';
      const { data } = await api.get(`/payment-verification/v2/supplier-analytics${params}`);
      setSupplierAnalytics(data);
    } catch (error) {
      console.error('Supplier analytics error:', error);
    } finally {
      setSupplierAnalyticsLoading(false);
    }
  };

  // Job Queue functions moved to Cockpit (ParserQueuePage.js & EmailQueuePage.js)
  // Bulk reprocess per-org (used in organisations tab)
  const handleBulkReprocessOrg = async (accountId, orgName) => {
    if (!window.confirm(`Are you sure you want to reprocess all unprocessed bills for "${orgName}"? This will send them to the Lambda processing queue.`)) return;
    setBulkReprocessLoading(true);
    setBulkReprocessOrgId(accountId);
    try {
      const { data } = await api.post(`/super-admin/accounts/${accountId}/bulk-reprocess`, {
        statuses: ['uploaded', 'processing', 'failed'],
        limit: 100
      });
      toast.success(`Sent ${data.sent_to_sqs} documents to SQS for reprocessing`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk reprocess');
    } finally {
      setBulkReprocessLoading(false);
      setBulkReprocessOrgId(null);
    }
  };

  const fetchAllUsers = async () => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams();
      if (usersFilters.role !== 'all') params.append('role', usersFilters.role);
      if (usersFilters.account_id !== 'all') params.append('account_id', usersFilters.account_id);
      if (usersFilters.status !== 'all') params.append('status', usersFilters.status);
      if (usersFilters.search) params.append('search', usersFilters.search);
      
      const { data } = await api.get(`/super-admin/all-users?${params.toString()}`);
      setAllUsers(data.users || []);
      setUsersTotalCount(data.total_count || 0);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  // Vendor Registry functions
  const fetchVendors = async () => {
    setVendorsLoading(true);
    try {
      const params = new URLSearchParams();
      if (vendorSearch) params.append('search', vendorSearch);
      const { data } = await api.get(`/ubm/vendors?${params.toString()}`);
      setVendors(data || []);
    } catch (error) {
      toast.error('Failed to load vendors');
      setVendors([]);
    } finally {
      setVendorsLoading(false);
    }
  };

  const fetchReviewQueue = async () => {
    setReviewQueueLoading(true);
    try {
      const { data } = await api.get('/ubm/vendors/review-queue');
      setReviewQueue(data || []);
    } catch (error) {
      console.error('Failed to load review queue:', error);
      setReviewQueue([]);
    } finally {
      setReviewQueueLoading(false);
    }
  };

  const handleReviewAction = async (vendorId, action, mergeIntoId = null) => {
    try {
      await api.put(`/ubm/vendors/${vendorId}/review`, { 
        action, 
        merge_into_vendor_id: mergeIntoId 
      });
      toast.success(`Vendor ${action}d successfully`);
      fetchReviewQueue();
      fetchVendors();
      setShowMergeModal(false);
      setMergeVendor(null);
    } catch (error) {
      toast.error(`Failed to ${action} vendor`);
    }
  };

  const resetVendorForm = () => {
    setVendorForm({
      vendor_name: '',
      vendor_code: '',
      email: '',
      support_phone: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      website: '',
      portal_url: '',
      commodities_served: [],
      payment_methods: {
        ach: { enabled: false },
        check: { enabled: true },
        credit_card: { enabled: false },
        wire: { enabled: false },
        account_pay: { enabled: false },
        auto_pay: { enabled: false },
      },
    });
    setEditingVendor(null);
  };

  const handleSaveVendor = async (e) => {
    e.preventDefault();
    if (!vendorForm.vendor_name) {
      toast.error('Vendor name is required');
      return;
    }
    try {
      if (editingVendor) {
        await api.put(`/ubm/vendors/${editingVendor.id}`, vendorForm);
        toast.success('Vendor updated');
      } else {
        await api.post('/ubm/vendors', vendorForm);
        toast.success('Vendor created');
      }
      setShowVendorModal(false);
      resetVendorForm();
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vendor');
    }
  };

  const handleDeleteVendor = async () => {
    if (!editingVendor) return;
    try {
      await api.delete(`/ubm/vendors/${editingVendor.id}`);
      toast.success('Vendor deleted');
      setShowDeleteVendor(false);
      setEditingVendor(null);
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete vendor');
    }
  };

  const openEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setVendorForm({
      vendor_name: vendor.vendor_name || '',
      vendor_code: vendor.vendor_code || '',
      email: vendor.email || '',
      support_phone: vendor.support_phone || '',
      address: vendor.address || '',
      city: vendor.city || '',
      state: vendor.state || '',
      zip: vendor.zip || '',
      website: vendor.website || '',
      portal_url: vendor.portal_url || '',
      commodities_served: vendor.commodities_served || [],
      payment_methods: vendor.payment_methods || {
        ach: { enabled: false },
        check: { enabled: true },
        credit_card: { enabled: false },
        wire: { enabled: false },
        account_pay: { enabled: false },
        auto_pay: { enabled: false },
      },
    });
    setShowVendorModal(true);
  };

  const toggleCommodity = (commodity) => {
    setVendorForm(prev => {
      const current = prev.commodities_served || [];
      if (current.includes(commodity)) {
        return { ...prev, commodities_served: current.filter(c => c !== commodity) };
      }
      return { ...prev, commodities_served: [...current, commodity] };
    });
  };

  const togglePaymentMethod = (method) => {
    setVendorForm(prev => ({
      ...prev,
      payment_methods: {
        ...prev.payment_methods,
        [method]: { ...prev.payment_methods[method], enabled: !prev.payment_methods[method]?.enabled },
      },
    }));
  };

  const handleUpdateUserStatus = async (userId, newStatus) => {
    const action = newStatus === 'active' ? 'activate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    try {
      await api.patch(`/super-admin/users/${userId}/status`, { status: newStatus });
      toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchAllUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleForceLogout = async (userId) => {
    if (!window.confirm('Force logout this user? They will need to login again.')) return;
    try {
      await api.post(`/super-admin/users/${userId}/force-logout`);
      toast.success('User has been logged out');
      fetchAllUsers();
    } catch (error) {
      toast.error('Failed to force logout user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/super-admin/users/${userId}`);
      toast.success('User deleted');
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Migration Key functions
  const generateMigrationKey = async () => {
    setMigrationKeyLoading(true);
    try {
      const { data } = await api.post('/super-admin/migration-key', {
        name: 'Database Migration',
        expires_hours: 24
      });
      setMigrationKey(data);
      toast.success('Migration key generated! Copy it now - it won\'t be shown again.');
    } catch (error) {
      toast.error('Failed to generate migration key');
    } finally {
      setMigrationKeyLoading(false);
    }
  };

  // UBM Sync functions
  const fetchUbmSyncStatus = async () => {
    setUbmSyncLoading(true);
    try {
      const { data } = await api.get('/ubm/sync/status');
      setUbmSyncStatus(data);
    } catch (error) {
      toast.error('Failed to load UBM sync status');
    } finally {
      setUbmSyncLoading(false);
    }
  };

  const fetchUbmSyncHistory = async () => {
    try {
      const { data } = await api.get('/ubm/sync/history?limit=10');
      setUbmSyncHistory(data);
    } catch (error) {
      console.error('Failed to load sync history', error);
    }
  };

  const handleUbmSync = async () => {
    setUbmSyncing(true);
    try {
      await api.post('/ubm/sync/run');
      toast.success('Sync started in background. Refresh in a few minutes to see results.');
      // Poll for completion
      setTimeout(() => { fetchUbmSyncStatus(); fetchUbmSyncHistory(); }, 30000);
    } catch (error) {
      toast.error('Failed to trigger sync');
    } finally {
      setUbmSyncing(false);
    }
  };

  const handleUbmCustomerSync = async (accountId, customerName) => {
    setUbmCustomerSyncing(accountId);
    try {
      await api.post(`/ubm/sync/customer/${accountId}`);
      toast.success(`Sync started for ${customerName}. Refresh in a moment.`);
      setTimeout(() => { fetchUbmSyncStatus(); fetchUbmSyncHistory(); }, 15000);
    } catch (error) {
      toast.error(`Failed to sync ${customerName}`);
    } finally {
      setUbmCustomerSyncing(null);
    }
  };

  // Sync Health Dashboard functions
  const fetchSyncProgress = async () => {
    try {
      const { data } = await api.get('/ubm/sync/progress');
      setSyncProgress(data);
    } catch (error) {
      console.error('Failed to fetch sync progress', error);
    }
  };

  const fetchSyncErrors = async () => {
    try {
      const { data } = await api.get('/ubm/sync/errors?limit=50');
      setSyncErrors(data);
    } catch (error) {
      console.error('Failed to fetch sync errors', error);
    }
  };

  const fetchSyncProgressHistory = async () => {
    try {
      const { data } = await api.get('/ubm/sync/progress/history?limit=10');
      setSyncProgressHistory(data);
    } catch (error) {
      console.error('Failed to fetch sync progress history', error);
    }
  };

  // Customer Report function
  const fetchCustomerReport = async () => {
    setCustomerReportLoading(true);
    try {
      const { data } = await api.get('/ubm/sync/status');
      setCustomerReport(data?.customer_stats || []);
    } catch (error) {
      toast.error('Failed to load customer report');
    } finally {
      setCustomerReportLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!createUserForm.email || !createUserForm.password || !createUserForm.name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setCreatingUser(true);
    try {
      // Create the user
      const { data } = await api.post('/super-admin/users', {
        email: createUserForm.email,
        password: createUserForm.password,
        name: createUserForm.name,
        role: createUserForm.role,
        is_super_admin: createUserForm.is_super_admin,
        is_super_pf_admin: createUserForm.is_super_pf_admin,
        is_ops_admin: createUserForm.is_ops_admin,
        is_ops_editor: createUserForm.is_ops_editor,
        is_ops_pf: createUserForm.is_ops_pf,
        is_finance: createUserForm.is_finance,
        is_support: createUserForm.is_support,
        default_page: createUserForm.default_page,
      });
      
      const userId = data.user_id;
      
      // Assign to selected accounts
      if (createUserForm.assigned_accounts.length > 0) {
        for (const accountId of createUserForm.assigned_accounts) {
          try {
            await api.post(`/super-admin/accounts/${accountId}/users`, {
              user_id: userId,
              role: createUserForm.role,
            });
          } catch (err) {
            console.error(`Failed to add user to account ${accountId}:`, err);
          }
        }
      }
      
      toast.success(`User ${createUserForm.name} created successfully`);
      setShowCreateUser(false);
      setCreateUserForm({
        name: '',
        email: '',
        password: '',
        role: 'viewer',
        is_super_admin: false,
        is_super_pf_admin: false,
        is_ops_admin: false,
        is_ops_editor: false,
        is_ops_pf: false,
        is_finance: false,
        is_support: false,
        assigned_accounts: [],
        default_page: '/bills/dashboard',
      });
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    // API returns `organizations: [{ id, name, role }]` (from /super-admin/all-users).
    // Earlier code read `user.accounts` which never exists on this payload, so
    // the modal always opened with zero orgs assigned even when the user had
    // valid memberships. Map `id` → `account_id` here.
    const orgList = user.organizations || user.accounts || [];
    const currentAccounts = orgList.map((o) => o.account_id || o.id).filter(Boolean);
    const accountRoles = orgList
      .map((o) => ({ account_id: o.account_id || o.id, role: o.role || 'viewer' }))
      .filter((r) => r.account_id);
    setEditUserForm({
      name: user.name || '',
      is_super_admin: user.is_super_admin || false,
      is_super_pf_admin: user.is_super_pf_admin || false,
      is_ops_admin: user.is_ops_admin || false,
      is_ops_editor: user.is_ops_editor || false,
      is_ops_pf: user.is_ops_pf || false,
      is_finance: user.is_finance || false,
      is_support: user.is_support || false,
      default_role: 'viewer',
      assigned_accounts: currentAccounts,
      account_roles: accountRoles,
      default_page: user.default_page || '/bills/dashboard',
    });
    setShowEditUser(true);
  };

  const handleUpdateUserAssignments = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    
    setUpdatingUser(true);
    try {
      // Use the new PUT endpoint to update user
      await api.put(`/super-admin/users/${editingUser.id}`, {
        name: editUserForm.name,
        is_super_admin: editUserForm.is_super_admin,
        is_super_pf_admin: editUserForm.is_super_pf_admin,
        is_ops_admin: editUserForm.is_ops_admin,
        is_ops_editor: editUserForm.is_ops_editor,
        is_ops_pf: editUserForm.is_ops_pf,
        is_finance: editUserForm.is_finance,
        is_support: editUserForm.is_support,
        assigned_accounts: editUserForm.assigned_accounts,
        default_role: editUserForm.default_role,
        account_roles: editUserForm.account_roles,
        default_page: editUserForm.default_page,
      });
      
      toast.success('User updated successfully');
      setShowEditUser(false);
      setEditingUser(null);
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setUpdatingUser(false);
    }
  };

  const updateAccountRole = (accountId, newRole) => {
    setEditUserForm(prev => {
      const existingRole = prev.account_roles.find(ar => ar.account_id === accountId);
      if (existingRole) {
        return {
          ...prev,
          account_roles: prev.account_roles.map(ar => 
            ar.account_id === accountId ? { ...ar, role: newRole } : ar
          )
        };
      } else {
        return {
          ...prev,
          account_roles: [...prev.account_roles, { account_id: accountId, role: newRole }]
        };
      }
    });
  };

  const getAccountRole = (accountId) => {
    const roleEntry = editUserForm.account_roles.find(ar => ar.account_id === accountId);
    return roleEntry?.role || editUserForm.default_role || 'viewer';
  };

  const toggleAccountAssignment = (accountId, formSetter, currentAssignments) => {
    if (currentAssignments.includes(accountId)) {
      formSetter(prev => ({
        ...prev,
        assigned_accounts: prev.assigned_accounts.filter(id => id !== accountId)
      }));
    } else {
      formSetter(prev => ({
        ...prev,
        assigned_accounts: [...prev.assigned_accounts, accountId]
      }));
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
      completed: 'bg-green-100 text-green-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getUserStatusBadge = (status) => {
    return status === 'active' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const getLoginStatusBadge = (status) => {
    return status === 'logged_in'
      ? 'bg-blue-100 text-blue-800'
      : 'bg-gray-100 text-gray-600';
  };

  const fetchGlobalConfig = async () => {
    try {
      const { data } = await api.get('/super-admin/config');
      if (data.email_config) {
        setEmailConfig(data.email_config);
      }
      if (data.ai_config) {
        setAiConfig(data.ai_config);
      }
      if (data.support_config) {
        setSupportConfig({
          support_email: data.support_config.support_email || 'support@billportal.io',
          cc_user: data.support_config.cc_user !== false,
        });
      }
    } catch (error) {
      console.error('Failed to load global config');
    }
  };

  const fetchSupportRequests = async () => {
    setLoadingSupportRequests(true);
    try {
      const { data } = await api.get('/super-admin/support/requests');
      setSupportRequests(data.requests || []);
    } catch (error) {
      console.error('Failed to load support requests');
    } finally {
      setLoadingSupportRequests(false);
    }
  };

  const updateSupportRequestStatus = async (requestId, status) => {
    try {
      await api.put(`/super-admin/support/requests/${requestId}/status`, { status });
      setSupportRequests((prev) => prev.map((r) => (r.id === requestId ? { ...r, status } : r)));
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  const fetchAccountDetails = async (accountId) => {
    try {
      const { data } = await api.get(`/super-admin/accounts/${accountId}`);
      setSelectedAccount(data.account);
      setAccountMembers(data.members || []);
    } catch (error) {
      // Surface the actual backend detail so production issues are diagnosable
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load account details${status ? ` (${status})` : ''}: ${detail}`);
      // Also log the full response for browser console in prod
      // eslint-disable-next-line no-console
      console.error('fetchAccountDetails failed', { status, detail, response: error.response?.data });
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      await api.post('/super-admin/accounts', accountForm);
      toast.success('Organisation created successfully');
      setAccountForm({ organisation_name: '', subscription_plan: 'free' });
      setShowCreateAccount(false);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organisation');
    }
  };

  const handleEditAccount = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/super-admin/accounts/${selectedAccount.id}`, editForm);
      toast.success('Organisation updated successfully');
      setShowEditAccount(false);
      fetchAccounts();
      fetchAccountDetails(selectedAccount.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update organisation');
    }
  };

  const handleToggleStatus = async (accountId) => {
    try {
      const { data } = await api.patch(`/super-admin/accounts/${accountId}/status`);
      toast.success(data.message);
      fetchAccounts();
      if (selectedAccount?.id === accountId) {
        fetchAccountDetails(accountId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle status');
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await api.delete(`/super-admin/accounts/${selectedAccount.id}`);
      toast.success('Organisation deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedAccount(null);
      setAccountMembers([]);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete organisation');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/super-admin/accounts/${selectedAccount.id}/users`, userForm);
      toast.success('User added to organisation');
      setUserForm({ name: '', email: '', password: '', role: 'viewer' });
      setShowAddUser(false);
      fetchAccountDetails(selectedAccount.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    }
  };

  const handleRemoveUser = async (userId) => {
    try {
      await api.delete(`/super-admin/accounts/${selectedAccount.id}/users/${userId}`);
      toast.success('User removed from organisation');
      fetchAccountDetails(selectedAccount.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  const openEditModal = async (account) => {
    setEditForm({
      organisation_name: account.organisation_name || account.company_name || '',
      account_no: account.account_no || '',
      subscription_plan: account.subscription_plan || 'free',
      bp_org_id: account.bp_org_id || null,
      bp_partner_id: account.bp_partner_id || null,
      subscription_start_date: (account.subscription_start_date || '').slice(0, 10),
      subscription_end_date: (account.subscription_end_date || '').slice(0, 10),
      subscription_status: account.subscription_status || 'open',
      missing_bill_start_date: (account.missing_bill_start_date || '').slice(0, 10),
      status: account.status || 'active',
      mask_customer_names: account.mask_customer_names || false,
      mask_service_addresses: account.mask_service_addresses || false,
      enabled_apps: {
        // Core apps are always enabled (locked in UI) — merge so even
        // legacy/partial DB records render them as checked.
        ...(account.enabled_apps || {}),
        portal_collection: true,
        bill_parsing: true,
      },
    });
    setEditDialogTab('details');
    setShowEditAccount(true);
    
    // Fetch NACHA originator config for this account
    fetchNachaOriginatorConfig(account.id);
  };

  const fetchNachaOriginatorConfig = async (accountId) => {
    try {
      const { data } = await api.get(`/super-admin/accounts/${accountId}/nacha-originator-config`);
      setNachaOriginatorConfig({
        company_name: data.company_name || '',
        company_tax_id: data.company_tax_id || '',
        odfi_routing_number: data.odfi_routing_number || '',
        odfi_account_number: data.odfi_account_number || '',
        odfi_account_name: data.odfi_account_name || '',
        odfi_bank_name: data.odfi_bank_name || '',
        immediate_destination_routing: data.immediate_destination_routing || '',
        immediate_destination_name: data.immediate_destination_name || '',
      });
    } catch {
      setNachaOriginatorConfig({
        company_name: '', company_tax_id: '', odfi_routing_number: '',
        odfi_account_number: '', odfi_account_name: '', odfi_bank_name: '',
        immediate_destination_routing: '', immediate_destination_name: '',
      });
    }
  };

  const handleSaveNachaConfig = async () => {
    if (!selectedAccount?.id) return;
    // Light client-side validation
    const taxId = (nachaOriginatorConfig.company_tax_id || '').replace(/\D/g, '');
    const routing = (nachaOriginatorConfig.odfi_routing_number || '').replace(/\D/g, '');
    const destRouting = (nachaOriginatorConfig.immediate_destination_routing || '').replace(/\D/g, '');
    if (taxId && taxId.length !== 9) { toast.error('Company Tax ID must be exactly 9 digits'); return; }
    if (routing && routing.length !== 9) { toast.error('ODFI Routing Number must be exactly 9 digits'); return; }
    if (destRouting && destRouting.length !== 9) { toast.error('Immediate Destination Routing must be exactly 9 digits'); return; }

    setSavingNachaConfig(true);
    try {
      await api.put(`/super-admin/accounts/${selectedAccount.id}/nacha-originator-config`, nachaOriginatorConfig);
      toast.success('ACH / NACHA originator configuration saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save ACH configuration');
    } finally {
      setSavingNachaConfig(false);
    }
  };

  const handleAppToggle = (appKey) => {
    setEditForm(prev => {
      const newApps = { ...prev.enabled_apps };
      
      // Portal Collection and Bill Parsing are always on
      if (appKey === 'portal_collection' || appKey === 'bill_parsing') {
        return prev;
      }
      
      // Toggle the app
      newApps[appKey] = !newApps[appKey];
      
      // If UBM is enabled, Bill Parsing must be enabled
      if (appKey === 'ubm' && newApps.ubm) {
        newApps.bill_parsing = true;
      }
      
      // If Bill Parsing is disabled, UBM must be disabled
      if (appKey === 'bill_parsing' && !newApps.bill_parsing) {
        newApps.ubm = false;
      }
      
      return { ...prev, enabled_apps: newApps };
    });
  };

  const handleSaveEmailConfig = async () => {
    setSavingEmail(true);
    try {
      await api.put('/super-admin/config/email', emailConfig);
      toast.success('Email configuration saved');
      fetchGlobalConfig();
    } catch (error) {
      toast.error('Failed to save email configuration');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleSaveSupportConfig = async () => {
    setSavingSupport(true);
    try {
      await api.put('/super-admin/config/support', supportConfig);
      toast.success('Support email configuration saved');
      fetchGlobalConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save support configuration');
    } finally {
      setSavingSupport(false);
    }
  };

  const handleSaveAiConfig = async () => {
    setSavingAi(true);
    try {
      await api.put('/super-admin/config/ai', aiConfig);
      toast.success('AI configuration saved');
      fetchGlobalConfig();
    } catch (error) {
      toast.error('Failed to save AI configuration');
    } finally {
      setSavingAi(false);
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  ];

  const moreMenuItems = [
    { id: 'organisations', label: 'Organisations', icon: Building2 },
    { id: 'vendor-registry', label: 'Vendor Registry', icon: Landmark },
    { id: 'all-users', label: 'User Management', icon: Users },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'export-reports', label: 'Export Reports', icon: Download },
    { id: 'email', label: 'Email Config', icon: Mail },
    { id: 'support-email', label: 'Support Email', icon: LifeBuoy },
    { id: 'ai', label: 'AI Model Setup', icon: Bot },
    { id: 'oauth', label: 'Social Sign-in', icon: Shield },
    { id: 'emission-factors', label: 'Emission Factors', icon: Leaf },
    ...(isOwner() ? [{ id: 'stripe-config', label: 'Stripe Config', icon: CreditCard }] : []),
    { id: 'outbound-ip', label: 'Outbound IP Config', icon: Network },
    { id: 'mysql-browser', label: 'DB Browser', icon: Database },
    { id: 'cross-customer', label: 'Cross-Customer Data', icon: AlertTriangle },
    { id: 'dev-tools', label: 'Dev Tools', icon: Settings, isNav: true, navPath: '/super-admin/dev-tools' },
    { id: 'intake-rollup', label: 'Intake Rollup', icon: ListChecks, isNav: true, navPath: '/super-admin/intake-rollup' },
    { id: 'regression-tests', label: 'Regression Tests', icon: ShieldCheck },
    { id: 'documentation', label: 'Documentation', icon: FileSearch },
    { id: 'payment-files', label: 'Payment Files', icon: CreditCard },
  ];

  const reportMenuItems = [
    { id: 'report-bill-stream', label: 'Bill Stream', icon: FileText },
    { id: 'report-missing-bills', label: 'Missing Bills', icon: AlertTriangle },
    { id: 'report-customer', label: 'Customer Report', icon: Building2 },
    { id: 'report-account-bill', label: 'Account & Bill Summary', icon: BarChart3 },
  ];

  // SA/Owner top-level Subscriptions + Finance menus (finance-only users get
  // their own scoped versions below). Kept out of the crowded "More…" menu so
  // they're discoverable at the Super Admin level.
  const saSubscriptionsMenuItems = [
    { id: 'sub-pricing', label: 'Subscription Pricing', icon: Wallet },
    { id: 'add-credits', label: 'Add Credits', icon: CreditCard },
    { id: 'topup-report', label: 'Credit Top-up Report', icon: Receipt },
    { id: 'billing-history', label: 'Customer Billing History', icon: History },
    { id: 'crm-sync', label: 'CRM Sync', icon: Radio },
    { id: 'backend-crm', label: 'Backend CRM', icon: Activity },
    { id: 'support-requests', label: 'Support Requests', icon: LifeBuoy },
    { id: 'registration', label: 'Registration', icon: UserPlus },
  ];
  const saFinanceMenuItems = [
    { id: 'sa_finance_report', label: 'Finance (Credit/Debit)', icon: DollarSign },
    { id: 'sa_finance_refunds', label: 'Finance Refunds', icon: Scale },
    { id: 'finance-invoices', label: 'Billing Invoices', icon: Receipt },
  ];

  // Finance-only users get a tightly scoped console: the Subscriptions menu
  // (minus Payment Configurations) + the Finance Credit/Debit report & Refunds.
  const financeOnly = isFinanceOnly();
  const supportOnly = isSupportOnly();
  const subscriptionsMenuItems = [
    { id: 'sub-pricing', label: 'Subscription Pricing', icon: Wallet },
    { id: 'add-credits', label: 'Add Credits', icon: CreditCard },
    { id: 'topup-report', label: 'Credit Top-up Report', icon: Receipt },
    { id: 'crm-sync', label: 'CRM Sync', icon: Radio },
    { id: 'registration', label: 'Registration', icon: UserPlus },
    { id: 'sa_finance_refunds', label: 'Refunds', icon: Scale },
  ];

  // Canonical 2026 model identifiers. These are the only strings the
  // Lambda parser knows how to route to a provider — keep this list as
  // the single source of truth and never let users type a free-form model
  // name (typos here yield silent HTTP 404s in the parser).
  const providerModels = {
    emergent: [
      'gpt-5.2', 'gpt-4o', 'gpt-4o-mini',
      'claude-sonnet-4-5', 'claude-opus-4-5', 'claude-haiku-4-5',
      'gemini-3-pro', 'gemini-3-flash',
    ],
    openai: ['gpt-5.2', 'gpt-4o', 'gpt-4o-mini', 'gpt-4.1', 'o3-mini'],
    anthropic: ['claude-sonnet-4-5', 'claude-opus-4-5', 'claude-haiku-4-5'],
    gemini: ['gemini-3-pro', 'gemini-3-flash', 'gemini-2.5-pro', 'gemini-2.5-flash'],
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Shield className="h-6 w-6" strokeWidth={2} />
            <h1 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Super Admin
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              className="rounded-none"
              onClick={() => navigate('/cockpit')}
            >
              <Gauge className="h-4 w-4 mr-2" />
              Cockpit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-none"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-none"
              onClick={() => { clearAuth(); window.location.href = '/login'; }}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs Navigation */}
      <nav className="border-b border-border bg-card">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex items-center gap-1">
            {/* Finance-only scoped nav: Subscriptions menu + Finance tab */}
            {financeOnly && (
              <>
                <button
                  onClick={() => setActiveTab('sa_finance_report')}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === 'sa_finance_report'
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="tab-finance"
                >
                  <DollarSign className="h-4 w-4" />
                  Finance
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                        subscriptionsMenuItems.some(item => item.id === activeTab)
                          ? 'border-primary text-foreground'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid="tab-subscriptions"
                    >
                      <Wallet className="h-4 w-4" />
                      Subscriptions
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52">
                    {subscriptionsMenuItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <DropdownMenuItem
                          key={item.id}
                          onClick={() => setActiveTab(item.id)}
                          className="cursor-pointer"
                          data-testid={`tab-${item.id}`}
                        >
                          <Icon className="h-4 w-4 mr-2" />
                          {item.label}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
            {/* Support-Team-only scoped nav: Dashboard + Support Requests */}
            {supportOnly && (
              <>
                <button
                  onClick={() => setActiveTab('dashboard')}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === 'dashboard'
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="tab-dashboard"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  Dashboard
                </button>
                <button
                  onClick={() => setActiveTab('support-requests')}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === 'support-requests'
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="tab-support-requests"
                >
                  <LifeBuoy className="h-4 w-4" />
                  Support Requests
                </button>
              </>
            )}
            {!isOpsPf() && !financeOnly && !supportOnly && tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
            
            {/* Subscriptions + Finance dropdowns (SA/Owner) */}
            {!isOpsPf() && !financeOnly && !supportOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                    saSubscriptionsMenuItems.some(item => item.id === activeTab)
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="tab-subscriptions"
                >
                  <Wallet className="h-4 w-4" />
                  Subscriptions
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                {saSubscriptionsMenuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className="cursor-pointer"
                      data-testid={`tab-${item.id}`}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {item.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            )}
            {!isOpsPf() && !financeOnly && !supportOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                    saFinanceMenuItems.some(item => item.id === activeTab)
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="tab-finance"
                >
                  <DollarSign className="h-4 w-4" />
                  Finance
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                {saFinanceMenuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className="cursor-pointer"
                      data-testid={`tab-${item.id}`}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {item.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            )}

            {/* Reports Dropdown Menu */}
            {!isOpsPf() && !financeOnly && !supportOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                    reportMenuItems.some(item => item.id === activeTab)
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="tab-reports"
                >
                  <FileText className="h-4 w-4" />
                  Reports
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {reportMenuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className="cursor-pointer"
                      data-testid={`tab-${item.id}`}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {item.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            )}

            {/* Payments Dropdown Menu */}
            {!financeOnly && !supportOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === 'sa_payments_dashboard' || activeTab === 'sa_payments_workspace' || activeTab === 'sa_global_credit_debit' || activeTab === 'sa_upload_credit_file' || activeTab === 'sa_process_refunds'
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="tab-sa-payments"
                >
                  <CreditCard className="h-4 w-4" />
                  Payments
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                <DropdownMenuItem
                  onClick={() => setActiveTab('sa_payments_dashboard')}
                  className="cursor-pointer"
                  data-testid="tab-sa-payments-report"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Payments Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setActiveTab('sa_payments_workspace')}
                  className="cursor-pointer"
                  data-testid="tab-sa-payments-workspace"
                >
                  <ClipboardList className="h-4 w-4 mr-2" />
                  Global Workspace
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setActiveTab('sa_global_credit_debit')}
                  className="cursor-pointer"
                  data-testid="tab-sa-global-credit-debit"
                >
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  Global Credit / Debit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setActiveTab('sa_process_refunds')}
                  className="cursor-pointer"
                  data-testid="tab-sa-process-refunds"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Process Refunds
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            )}

            {/* AI Manager Dropdown - placed right before "More..." */}
            {!isOpsPf() && !financeOnly && !supportOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 border-transparent text-muted-foreground hover:text-foreground"
                  data-testid="tab-ai-manager"
                >
                  <Sparkles className="h-4 w-4" />
                  AI Manager
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                <DropdownMenuItem
                  onClick={() => navigate('/super-admin/ai-manager#overview')}
                  className="cursor-pointer"
                  data-testid="tab-ai-manager-overview"
                >
                  <LayoutDashboard className="h-4 w-4 mr-2" /> Overview
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/super-admin/ai-manager#agents')}
                  className="cursor-pointer"
                  data-testid="tab-ai-manager-agents"
                >
                  <Users className="h-4 w-4 mr-2" /> Agents
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/super-admin/ai-manager#email-queue')}
                  className="cursor-pointer"
                  data-testid="tab-ai-manager-email-queue"
                >
                  <Mail className="h-4 w-4 mr-2" /> Email Queue
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/super-admin/ai-manager#jobs')}
                  className="cursor-pointer"
                  data-testid="tab-ai-manager-jobs"
                >
                  <ListChecks className="h-4 w-4 mr-2" /> Jobs
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/super-admin/ai-manager#invocations')}
                  className="cursor-pointer"
                  data-testid="tab-ai-manager-invocations"
                >
                  <Activity className="h-4 w-4 mr-2" /> Invocations
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/super-admin/ai-manager#service-health')}
                  className="cursor-pointer"
                  data-testid="tab-ai-manager-service-health"
                >
                  <Heart className="h-4 w-4 mr-2" /> Service Health
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            )}

            {/* More... Dropdown Menu */}
            {!isOpsPf() && !financeOnly && !supportOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                    moreMenuItems.some(item => item.id === activeTab)
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="tab-more"
                >
                  <MoreHorizontal className="h-4 w-4" />
                  More...
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {moreMenuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem
                      key={item.id}
                      onClick={() => {
                        if (item.isNav && item.navPath) {
                          navigate(item.navPath);
                        } else {
                          setActiveTab(item.id);
                        }
                      }}
                      className="cursor-pointer"
                      data-testid={`tab-${item.id}`}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {item.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            )}
          </div>
        </div>
      </nav>

      <main
        className={
          // Wide tabs (payments workspace, global credit/debit, process refunds)
          // need full viewport width like Org > PF Bills. All other tabs stay
          // in the standard container.
          ['sa_payments_workspace', 'sa_global_credit_debit', 'sa_process_refunds'].includes(activeTab)
            ? 'px-4 md:px-6 py-8'
            : 'container mx-auto px-6 md:px-12 py-8'
        }
      >
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div>
            {/* Header with Time Range Filter */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <BarChart3 className="h-6 w-6 text-primary" />
                <div>
                  <h2 className="text-xl font-bold">Platform Dashboard</h2>
                  <p className="text-sm text-muted-foreground">Bill processing overview across all organizations</p>
                </div>
                <button
                  onClick={() => setShowSmsSheet(true)}
                  className="inline-flex items-center gap-1.5 px-3 h-9 border border-border rounded-none hover:bg-muted transition-colors ml-2 text-xs font-medium"
                  data-testid="sms-sheet-trigger"
                  title="SMS Messages"
                >
                  <Smartphone className="h-4 w-4" />
                  SMS
                </button>
              </div>
              <div className="flex items-center gap-3">
                <select
                  className="px-3 py-2 border border-border rounded-none bg-background text-sm"
                  value={dashboardTimeRange}
                  onChange={(e) => setDashboardTimeRange(e.target.value)}
                  data-testid="dashboard-time-range"
                >
                  <option value="30">Last 30 Days</option>
                  <option value="90">Last 3 Months</option>
                  <option value="180">Last 6 Months</option>
                  <option value="365">Last Year</option>
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-none"
                  onClick={fetchDashboardStats}
                  disabled={dashboardLoading}
                >
                  <RefreshCw className={`h-4 w-4 ${dashboardLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {/* Supplier Analytics Cards (TOP) */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Supplier Analytics</h3>
                <Select value={supplierPartnerId} onValueChange={(v) => { setSupplierPartnerId(v); fetchSupplierAnalytics(v); }}>
                  <SelectTrigger className="w-56 rounded-none h-9" data-testid="supplier-partner-filter">
                    <Building2 className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                    <SelectValue placeholder="Filter by Partner" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="all" className="rounded-none">All Partners</SelectItem>
                    {bpPartners.map(p => (
                      <SelectItem key={p.id} value={String(p.id)} className="rounded-none">{p.name} ({p.customer_count})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Card 1: Top 20 Suppliers by Volume */}
              <div className="border border-border rounded-none bg-card" data-testid="suppliers-by-volume-card">
                <div className="p-4 border-b border-border flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-sm">Top 20 Suppliers by Volume</h3>
                  </div>
                  <div className="flex items-center gap-3">
                    {supplierAnalytics?.summary && (
                      <span className="text-xs text-muted-foreground">
                        {supplierAnalytics.summary.total_suppliers} suppliers | {supplierAnalytics.summary.total_accounts} accounts
                      </span>
                    )}
                    <button
                      onClick={() => {
                        const pid = supplierPartnerId && supplierPartnerId !== 'all' ? `?partner_id=${supplierPartnerId}` : '';
                        const link = document.createElement('a');
                        link.href = `${process.env.REACT_APP_BACKEND_URL}/api/payment-verification/v2/supplier-analytics/csv${pid}`;
                        link.download = 'supplier_analytics.csv';
                        link.click();
                      }}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border border-border rounded-none hover:bg-muted transition-colors"
                      data-testid="download-suppliers-csv"
                      title="Download all suppliers as CSV"
                    >
                      <Download className="h-3.5 w-3.5" />
                      CSV
                    </button>
                  </div>
                </div>
                <div className="p-4 max-h-[500px] overflow-y-auto">
                  {supplierAnalyticsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : supplierAnalytics?.by_volume?.length > 0 ? (
                    <div className="space-y-1">
                      {supplierAnalytics.by_volume.map((s, i) => {
                        const maxAccounts = supplierAnalytics.by_volume[0]?.total_accounts || 1;
                        const barWidth = Math.max(8, (s.total_accounts / maxAccounts) * 100);
                        return (
                          <div key={s.supplier} className="group relative" data-testid={`volume-supplier-${i}`}>
                            <div className="flex items-center gap-3 py-1.5 px-2 hover:bg-muted/50 transition-colors cursor-default">
                              <span className="text-xs font-mono text-muted-foreground w-5 text-right">{i + 1}</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-0.5">
                                  <span className="text-sm font-medium truncate">{s.supplier}</span>
                                  <div className="flex items-center gap-2 ml-2 shrink-0">
                                    <span className="text-xs text-muted-foreground">{s.org_count} org{s.org_count > 1 ? 's' : ''}</span>
                                    <span className="text-sm font-bold tabular-nums">{s.total_accounts.toLocaleString()}</span>
                                  </div>
                                </div>
                                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary/70 rounded-full transition-all"
                                    style={{ width: `${barWidth}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                            {/* Hover tooltip */}
                            <div className="absolute left-full top-0 ml-2 z-50 hidden group-hover:block w-72">
                              <div className="bg-popover border border-border shadow-lg rounded-none p-3">
                                <p className="text-xs font-semibold mb-2 border-b pb-1">{s.supplier} — Org Breakdown</p>
                                <div className="space-y-1 max-h-48 overflow-y-auto">
                                  {s.orgs.map(o => (
                                    <div key={o.name} className="flex justify-between text-xs">
                                      <span className="truncate mr-2">{o.name}</span>
                                      <span className="shrink-0 font-mono">
                                        {o.accounts} <span className="text-muted-foreground">({o.pct_of_org}%)</span>
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-center text-sm text-muted-foreground py-8">No supplier data available</p>
                  )}
                </div>
              </div>

              {/* Card 2: Top 20 Suppliers by Cross-Org Commonality */}
              <div className="border border-border rounded-none bg-card" data-testid="suppliers-by-commonality-card">
                <div className="p-4 border-b border-border flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Network className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-sm">Top 20 Suppliers by Cross-Org Presence</h3>
                  </div>
                  <div className="flex items-center gap-3">
                    {supplierAnalytics?.summary && (
                      <span className="text-xs text-muted-foreground">
                        {supplierAnalytics.summary.total_customers} orgs
                      </span>
                    )}
                    <button
                      onClick={() => {
                        const pid = supplierPartnerId && supplierPartnerId !== 'all' ? `?partner_id=${supplierPartnerId}` : '';
                        const link = document.createElement('a');
                        link.href = `${process.env.REACT_APP_BACKEND_URL}/api/payment-verification/v2/supplier-analytics/csv${pid}`;
                        link.download = 'supplier_analytics.csv';
                        link.click();
                      }}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border border-border rounded-none hover:bg-muted transition-colors"
                      data-testid="download-suppliers-csv-2"
                      title="Download all suppliers as CSV"
                    >
                      <Download className="h-3.5 w-3.5" />
                      CSV
                    </button>
                  </div>
                </div>
                <div className="p-4 max-h-[500px] overflow-y-auto">
                  {supplierAnalyticsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : supplierAnalytics?.by_commonality?.length > 0 ? (
                    <div className="space-y-1">
                      {supplierAnalytics.by_commonality.map((s, i) => {
                        const maxOrgs = supplierAnalytics.by_commonality[0]?.org_count || 1;
                        const barWidth = Math.max(8, (s.org_count / maxOrgs) * 100);
                        return (
                          <div key={s.supplier} className="group relative" data-testid={`common-supplier-${i}`}>
                            <div className="flex items-center gap-3 py-1.5 px-2 hover:bg-muted/50 transition-colors cursor-default">
                              <span className="text-xs font-mono text-muted-foreground w-5 text-right">{i + 1}</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-0.5">
                                  <span className="text-sm font-medium truncate">{s.supplier}</span>
                                  <div className="flex items-center gap-2 ml-2 shrink-0">
                                    <Badge variant="outline" className="rounded-none text-xs px-1.5 py-0">{s.org_count} orgs</Badge>
                                    <span className="text-xs text-muted-foreground">{s.total_accounts.toLocaleString()} accts</span>
                                  </div>
                                </div>
                                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-blue-500/70 rounded-full transition-all"
                                    style={{ width: `${barWidth}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                            {/* Hover tooltip */}
                            <div className="absolute right-full top-0 mr-2 z-50 hidden group-hover:block w-72">
                              <div className="bg-popover border border-border shadow-lg rounded-none p-3">
                                <p className="text-xs font-semibold mb-2 border-b pb-1">{s.supplier} — {s.org_count} Organizations</p>
                                <div className="space-y-1 max-h-48 overflow-y-auto">
                                  {s.orgs.map(o => (
                                    <div key={o.name} className="flex justify-between text-xs">
                                      <span className="truncate mr-2">{o.name}</span>
                                      <span className="shrink-0 font-mono font-medium">{o.accounts} accounts</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-center text-sm text-muted-foreground py-8">No cross-org supplier data</p>
                  )}
                </div>
              </div>
              </div>
            </div>

            <div ref={dashboardStatsRef}>
            {dashboardLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : dashboardStats ? (
              <>
                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <div className="border border-border rounded-none p-6 bg-card">
                    <FileText className="h-8 w-8 mb-2 text-primary" />
                    <p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">
                      Total Received
                    </p>
                    <p className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {dashboardStats.summary?.total_received || 0}
                    </p>
                  </div>
                  <div className="border border-border rounded-none p-6 bg-card">
                    <CheckCircle className="h-8 w-8 mb-2 text-green-600" />
                    <p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">
                      Completed
                    </p>
                    <p className="text-4xl font-black tracking-tighter text-green-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {dashboardStats.summary?.total_completed || 0}
                    </p>
                  </div>
                  <div className="border border-border rounded-none p-6 bg-card">
                    <AlertCircle className="h-8 w-8 mb-2 text-red-600" />
                    <p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">
                      Failed
                    </p>
                    <p className="text-4xl font-black tracking-tighter text-red-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {dashboardStats.summary?.total_failed || 0}
                    </p>
                  </div>
                  <div className="border border-border rounded-none p-6 bg-card">
                    <Clock className="h-8 w-8 mb-2 text-blue-600" />
                    <p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">
                      Completion Rate
                    </p>
                    <p className="text-4xl font-black tracking-tighter text-blue-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {dashboardStats.summary?.completion_rate || 0}%
                    </p>
                  </div>
                </div>

                {/* System Health Card */}
                <div className="mb-8">
                  <div className="border border-border rounded-none bg-card p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Cpu className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold">System Health</h3>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-none h-8"
                        onClick={fetchSystemHealth}
                        disabled={systemHealthLoading}
                        data-testid="refresh-system-health-btn"
                      >
                        <RefreshCw className={`h-3 w-3 ${systemHealthLoading ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      {/* Outbound IP */}
                      <div className="flex items-center justify-between p-3 bg-muted/30 border border-border">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">Outbound IP</span>
                        </div>
                        {systemHealthLoading ? (
                          <Badge variant="secondary" className="rounded-none animate-pulse">Loading...</Badge>
                        ) : systemHealth?.outbound_ip ? (
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant="default" 
                              className="rounded-none font-mono text-xs cursor-pointer hover:bg-primary/80"
                              onClick={() => copyToClipboard(systemHealth.outbound_ip)}
                              data-testid="outbound-ip-badge"
                            >
                              {systemHealth.outbound_ip}
                              <Copy className="h-3 w-3 ml-1" />
                            </Badge>
                          </div>
                        ) : (
                          <Badge variant="secondary" className="rounded-none">Unknown</Badge>
                        )}
                      </div>
                      
                      {/* BP Postgres Connection */}
                      <div className="flex items-center justify-between p-3 bg-muted/30 border border-border">
                        <div className="flex items-center gap-2">
                          <Landmark className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">BP Postgres</span>
                        </div>
                        {systemHealthLoading ? (
                          <Badge variant="secondary" className="rounded-none animate-pulse">Checking...</Badge>
                        ) : systemHealth?.external_postgres?.status === 'connected' ? (
                          <Badge 
                            variant="default" 
                            className="rounded-none bg-green-600 hover:bg-green-700 cursor-pointer"
                            onClick={() => setShowPgErrorDialog(true)}
                            data-testid="pg-status-badge"
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Connected
                          </Badge>
                        ) : systemHealth?.external_postgres?.status === 'not_configured' ? (
                          <Badge 
                            variant="secondary" 
                            className="rounded-none cursor-pointer"
                            onClick={() => setShowPgErrorDialog(true)}
                            data-testid="pg-status-badge"
                          >
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Not Configured
                          </Badge>
                        ) : (
                          <Badge 
                            variant="destructive" 
                            className="rounded-none cursor-pointer hover:bg-destructive/80"
                            onClick={() => setShowPgErrorDialog(true)}
                            data-testid="pg-status-badge"
                          >
                            <XCircle className="h-3 w-3 mr-1" />
                            Disconnected
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Quick hint */}
                    <p className="text-xs text-muted-foreground mt-2">
                      Click on the status badge to see connection details
                    </p>
                  </div>
                </div>

                {/* Bill Processing Summary Table */}
                <div className="mb-8">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Bill Processing Summary by Organization
                  </h3>
                  <div className="border border-border rounded-none bg-card overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="bill-processing-table">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Organization</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Received</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Completed</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Failed</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Via Email</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Via API</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Via Upload</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Avg Time (s)</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardStats.by_organisation?.length > 0 ? (
                            dashboardStats.by_organisation.map((org, index) => (
                              <tr key={org.account_id || index} className="border-b border-border hover:bg-muted/30">
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    <Building2 className="h-4 w-4 text-muted-foreground" />
                                    <span className="font-medium">{org.organisation_name}</span>
                                  </div>
                                </td>
                                <td className="p-3 text-center font-mono">{org.total_received}</td>
                                <td className="p-3 text-center font-mono text-green-600">{org.completed}</td>
                                <td className="p-3 text-center font-mono text-red-600">{org.failed}</td>
                                <td className="p-3 text-center">
                                  <Badge variant="outline" className="rounded-none text-blue-600">
                                    <Mail className="h-3 w-3 mr-1" />
                                    {org.via_email}
                                  </Badge>
                                </td>
                                <td className="p-3 text-center">
                                  <Badge variant="outline" className="rounded-none text-purple-600">
                                    <Cpu className="h-3 w-3 mr-1" />
                                    {org.via_api}
                                  </Badge>
                                </td>
                                <td className="p-3 text-center">
                                  <Badge variant="outline" className="rounded-none text-green-600">
                                    {org.via_upload}
                                  </Badge>
                                </td>
                                <td className="p-3 text-center font-mono text-sm">{org.avg_processing_seconds || '-'}</td>
                                <td className="p-3 text-center">
                                  <Badge className={`rounded-none ${org.completion_rate >= 90 ? 'bg-green-600' : org.completion_rate >= 70 ? 'bg-yellow-500' : 'bg-red-600'}`}>
                                    {org.completion_rate}%
                                  </Badge>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={9} className="text-center p-8 text-muted-foreground">
                                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                <p>No bill processing data in selected period</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Bill Queue by Organization Table */}
                <div className="mb-8">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <ClipboardList className="h-5 w-5" />
                    Bill Queue by Organization
                  </h3>
                  <div className="border border-border rounded-none bg-card overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="bill-queue-table">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Organization</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Queued</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Processing</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Failed</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Assigned</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Total Pending</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardStats.queue_by_organisation?.length > 0 ? (
                            dashboardStats.queue_by_organisation.map((org, index) => (
                              <tr key={org.account_id || index} className="border-b border-border hover:bg-muted/30">
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    <Building2 className="h-4 w-4 text-muted-foreground" />
                                    <span className="font-medium">{org.organisation_name}</span>
                                  </div>
                                </td>
                                <td className="p-3 text-center">
                                  <Badge variant="secondary" className="rounded-none">{org.queued}</Badge>
                                </td>
                                <td className="p-3 text-center">
                                  <Badge className="rounded-none bg-blue-500">{org.processing}</Badge>
                                </td>
                                <td className="p-3 text-center">
                                  <Badge variant="destructive" className="rounded-none">{org.failed}</Badge>
                                </td>
                                <td className="p-3 text-center font-mono">{org.assigned}</td>
                                <td className="p-3 text-center">
                                  <Badge className={`rounded-none ${org.total_pending > 10 ? 'bg-orange-500' : org.total_pending > 0 ? 'bg-yellow-500' : 'bg-green-600'}`}>
                                    {org.total_pending}
                                  </Badge>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={6} className="text-center p-8 text-muted-foreground">
                                <CheckCircle className="h-8 w-8 mx-auto mb-2 opacity-50 text-green-500" />
                                <p>No pending bills in queue</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Team Activity Table */}
                <div>
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Team Activity (Top 20)
                  </h3>
                  <div className="border border-border rounded-none bg-card overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="team-activity-table">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">User</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Edits</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Approvals</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Assignments</th>
                            <th className="text-center text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Total Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardStats.team_activity?.length > 0 ? (
                            dashboardStats.team_activity.map((user, index) => (
                              <tr key={user.user_id || index} className="border-b border-border hover:bg-muted/30">
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                      <Star className="h-4 w-4 text-primary" />
                                    </div>
                                    <span className="font-medium">{user.user_name}</span>
                                  </div>
                                </td>
                                <td className="p-3 text-center font-mono">{user.edits}</td>
                                <td className="p-3 text-center font-mono text-green-600">{user.approvals}</td>
                                <td className="p-3 text-center font-mono text-blue-600">{user.assignments}</td>
                                <td className="p-3 text-center">
                                  <Badge className="rounded-none">{user.total_actions}</Badge>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className="text-center p-8 text-muted-foreground">
                                <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                <p>No team activity in selected period</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <RefreshCw className="h-8 w-8 animate-spin mb-4 opacity-50" />
                <p>Loading dashboard data...</p>
              </div>
            )}
            </div>

            {/* SMS Sheet (slide-out) */}
            <Sheet open={showSmsSheet} onOpenChange={setShowSmsSheet}>
              <SheetContent side="right" className="w-[420px] sm:w-[420px] p-0 rounded-none">
                <SheetHeader className="px-4 pt-4 pb-2">
                  <SheetTitle className="text-sm font-semibold">SMS Messages</SheetTitle>
                </SheetHeader>
                <div className="h-[calc(100vh-60px)]">
                  <SmsPhoneCard />
                </div>
              </SheetContent>
            </Sheet>

          </div>
        )}
                
        {/* Organisations Tab */}
        {/* Organisations Tab */}
        {activeTab === 'organisations' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="border border-border rounded-none p-6 bg-card">
                <Building2 className="h-8 w-8 mb-2 text-primary" />
                <p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">
                  Total Organisations
                </p>
                <p className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {accounts.length}
                </p>
              </div>
              <div className="border border-border rounded-none p-6 bg-card">
                <Building2 className="h-8 w-8 mb-2 text-green-600" />
                <p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">
                  Active
                </p>
                <p className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {accounts.filter(a => a.status === 'active').length}
                </p>
              </div>
              <div className="border border-border rounded-none p-6 bg-card">
                <Users className="h-8 w-8 mb-2 text-blue-600" />
                <p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">
                  Total Members
                </p>
                <p className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {accounts.reduce((sum, a) => sum + (a.member_count || 0), 0)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Organisations List */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold tracking-tight">Organisations</h2>
                  <Button
                    onClick={() => setShowCreateAccount(true)}
                    className="rounded-none font-bold uppercase"
                    data-testid="create-account-button"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Organisation
                  </Button>
                </div>

                {/* Search + Partner filter */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="relative flex-1">
                    <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <Input
                      value={orgSearchQuery}
                      onChange={(e) => setOrgSearchQuery(e.target.value)}
                      placeholder="Search by organisation name or account no…"
                      className="rounded-none pl-8 h-9 text-sm"
                      data-testid="org-search-input"
                    />
                  </div>
                  <Popover open={partnerFilterOpen} onOpenChange={setPartnerFilterOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={partnerFilterOpen}
                        className="rounded-none h-9 min-w-[200px] justify-between text-sm font-normal"
                        data-testid="org-partner-filter-trigger"
                      >
                        <span className="truncate">
                          {orgPartnerFilter === 'all' ? 'All partners' :
                           orgPartnerFilter === 'none' ? 'No partner' :
                           (bpPartners.find(p => String(p.id) === String(orgPartnerFilter))?.name || `Partner #${orgPartnerFilter}`)}
                        </span>
                        <ChevronDown className="h-4 w-4 opacity-50 ml-2 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 rounded-none w-[280px]" align="end">
                      <Command>
                        <CommandInput placeholder="Search partner…" className="h-9" data-testid="org-partner-search" />
                        <CommandList>
                          <CommandEmpty>No partner found.</CommandEmpty>
                          <CommandGroup>
                            <CommandItem
                              value="__all__ all partners"
                              onSelect={() => { setOrgPartnerFilter('all'); setPartnerFilterOpen(false); }}
                              className="rounded-none cursor-pointer"
                            >
                              <Check className={`mr-2 h-4 w-4 ${orgPartnerFilter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                              All partners
                            </CommandItem>
                            <CommandItem
                              value="__none__ no partner unassigned"
                              onSelect={() => { setOrgPartnerFilter('none'); setPartnerFilterOpen(false); }}
                              className="rounded-none cursor-pointer"
                            >
                              <Check className={`mr-2 h-4 w-4 ${orgPartnerFilter === 'none' ? 'opacity-100' : 'opacity-0'}`} />
                              No partner (unassigned)
                            </CommandItem>
                          </CommandGroup>
                          <CommandGroup heading="Partners">
                            {bpPartners.map((p) => (
                              <CommandItem
                                key={p.id}
                                value={`${p.name} ${p.id}`}
                                onSelect={() => { setOrgPartnerFilter(String(p.id)); setPartnerFilterOpen(false); }}
                                className="rounded-none cursor-pointer"
                                data-testid={`org-partner-option-${p.id}`}
                              >
                                <Check className={`mr-2 h-4 w-4 ${String(orgPartnerFilter) === String(p.id) ? 'opacity-100' : 'opacity-0'}`} />
                                {p.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Results count */}
                <p className="text-xs text-muted-foreground mb-2" data-testid="org-filter-count">
                  Showing <strong>{filteredAccounts.length}</strong> of {accounts.length} organisations
                  {(orgSearchQuery || orgPartnerFilter !== 'all') && (
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 ml-2 text-xs"
                      onClick={() => { setOrgSearchQuery(''); setOrgPartnerFilter('all'); }}
                      data-testid="org-filter-reset"
                    >
                      Reset filters
                    </Button>
                  )}
                </p>

                <div className="space-y-3">
                  {filteredAccounts.map((account) => (
                    <div
                      key={account.id}
                      className={`border rounded-none p-4 bg-card cursor-pointer transition-colors ${
                        selectedAccount?.id === account.id ? 'border-primary' : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => fetchAccountDetails(account.id)}
                      data-testid={`account-${account.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-mono text-xs font-bold text-muted-foreground">{account.account_no}</h3>
                            <Badge variant={account.status === 'active' ? 'default' : 'secondary'} className="rounded-none text-xs">
                              {account.status}
                            </Badge>
                          </div>
                          <p className="text-lg font-bold mt-1">{account.organisation_name || account.company_name}</p>
                          <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="uppercase">Plan: {account.subscription_plan}</span>
                            <span className="uppercase">Members: {account.member_count || 0}</span>
                            {account.bp_partner_id && (
                              <span className="uppercase text-blue-600">
                                BP: {bpPartners.find(p => p.id === account.bp_partner_id)?.name || `Partner #${account.bp_partner_id}`}
                                {account.bp_org_id && ` / Org #${account.bp_org_id}`}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 rounded-none text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            onClick={(e) => { e.stopPropagation(); handleBulkReprocessOrg(account.id, account.organisation_name || account.company_name); }}
                            disabled={bulkReprocessLoading && bulkReprocessOrgId === account.id}
                            title="Reprocess All Unprocessed Bills"
                            data-testid={`bulk-reprocess-${account.id}`}
                          >
                            {bulkReprocessLoading && bulkReprocessOrgId === account.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Zap className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 rounded-none"
                            onClick={(e) => { e.stopPropagation(); fetchAccountDetails(account.id); openEditModal(account); }}
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-8 w-8 p-0 rounded-none ${account.status === 'active' ? 'text-green-600' : 'text-muted-foreground'}`}
                            onClick={(e) => { e.stopPropagation(); handleToggleStatus(account.id); }}
                            title={account.status === 'active' ? 'Deactivate' : 'Activate'}
                          >
                            <Power className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {filteredAccounts.length === 0 && (
                    <div className="border border-dashed border-border rounded-none p-8 text-center text-muted-foreground">
                      {accounts.length === 0
                        ? 'No organisations yet. Create your first one.'
                        : 'No organisations match the current filters.'}
                    </div>
                  )}
                </div>
              </div>

              {/* Organisation Details */}
              <div>
                {selectedAccount ? (
                  <div className="border border-border rounded-none bg-card">
                    <div className="p-6 border-b border-border">
                      <div className="flex items-start justify-between">
                        <div>
                          <h2 className="text-2xl font-bold">{selectedAccount.organisation_name || selectedAccount.company_name}</h2>
                          <p className="text-sm text-muted-foreground font-mono">{selectedAccount.account_no}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-none"
                            onClick={() => openEditModal(selectedAccount)}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            className="rounded-none"
                            onClick={() => setShowDeleteConfirm(true)}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                      <div className="flex gap-4 mt-4">
                        <Badge variant={selectedAccount.status === 'active' ? 'default' : 'secondary'} className="rounded-none">
                          {selectedAccount.status}
                        </Badge>
                        <span className="text-sm text-muted-foreground">Plan: {selectedAccount.subscription_plan}</span>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold">Members ({accountMembers.length})</h3>
                        <Button
                          size="sm"
                          className="rounded-none"
                          onClick={() => setShowAddUser(true)}
                        >
                          <UserPlus className="h-4 w-4 mr-1" />
                          Add User
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {accountMembers.map((member) => (
                          <div key={member.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-none">
                            <div>
                              <p className="font-medium">{member.name}</p>
                              <p className="text-sm text-muted-foreground">{member.email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="rounded-none uppercase text-xs">
                                {member.role}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 rounded-none text-destructive hover:text-destructive"
                                onClick={() => handleRemoveUser(member.user_id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        {accountMembers.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-4">No members in this organisation</p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="border border-dashed border-border rounded-none p-12 text-center">
                    <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1.5} />
                    <p className="text-muted-foreground">Select an organisation to view details</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Job Queues moved to Cockpit */}

        {/* All Users Tab */}
        {activeTab === 'all-users' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Users className="h-6 w-6 text-primary" />
                <div>
                  <h2 className="text-xl font-bold">User Management</h2>
                  <p className="text-sm text-muted-foreground">
                    {usersTotalCount} user{usersTotalCount !== 1 ? 's' : ''} across all organizations
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-none"
                  onClick={fetchAllUsers}
                  disabled={usersLoading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${usersLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button
                  size="sm"
                  className="rounded-none"
                  onClick={() => setShowCreateUser(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create User
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="w-48">
                <Label className="text-xs uppercase tracking-wide font-medium mb-1 block">Search</Label>
                <Input
                  placeholder="Name or email..."
                  className="rounded-none"
                  value={usersFilters.search}
                  onChange={(e) => setUsersFilters({ ...usersFilters, search: e.target.value })}
                />
              </div>
              <div className="w-48">
                <Label className="text-xs uppercase tracking-wide font-medium mb-1 block">Organisation</Label>
                <Select
                  value={usersFilters.account_id}
                  onValueChange={(value) => setUsersFilters({ ...usersFilters, account_id: value })}
                >
                  <SelectTrigger className="rounded-none">
                    <SelectValue placeholder="All Organisations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organisations</SelectItem>
                    {accounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.organisation_name || account.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-36">
                <Label className="text-xs uppercase tracking-wide font-medium mb-1 block">Role</Label>
                <Select
                  value={usersFilters.role}
                  onValueChange={(value) => setUsersFilters({ ...usersFilters, role: value })}
                >
                  <SelectTrigger className="rounded-none">
                    <SelectValue placeholder="All Roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-36">
                <Label className="text-xs uppercase tracking-wide font-medium mb-1 block">Status</Label>
                <Select
                  value={usersFilters.status}
                  onValueChange={(value) => setUsersFilters({ ...usersFilters, status: value })}
                >
                  <SelectTrigger className="rounded-none">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Users Table */}
            <div className="border border-border rounded-none bg-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">User</th>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Role</th>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Organization(s)</th>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Status</th>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Login Status</th>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">AI Agent</th>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Last Login</th>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Created</th>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersLoading ? (
                      <tr>
                        <td colSpan={9} className="text-center p-8 text-muted-foreground">
                          <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                          Loading users...
                        </td>
                      </tr>
                    ) : allUsers.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center p-8 text-muted-foreground">
                          <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>No users found</p>
                        </td>
                      </tr>
                    ) : (
                      allUsers.map((user) => (
                        <tr key={user.id} className="border-b border-border hover:bg-muted/30">
                          <td className="p-3">
                            <div className="max-w-[360px]">
                              <p className="font-medium truncate" title={user.name}>{user.name}</p>
                              <p className="text-xs text-muted-foreground truncate" title={user.email}>{user.email}</p>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex flex-wrap gap-1">
                              {user.is_owner && (
                                <Badge className="rounded-none text-xs bg-amber-100 text-amber-900 border border-amber-400" data-testid={`owner-badge-${user.id}`}>Owner</Badge>
                              )}
                              {user.is_super_admin && !user.is_owner && (
                                <Badge className="rounded-none text-xs bg-purple-100 text-purple-800">Super Admin</Badge>
                              )}
                              {user.is_super_pf_admin && (
                                <Badge className="rounded-none text-xs bg-blue-100 text-blue-800">Super PF Admin</Badge>
                              )}
                              {user.is_ops_admin && (
                                <Badge className="rounded-none text-xs bg-teal-100 text-teal-800">Ops Admin</Badge>
                              )}
                              {user.is_ops_editor && (
                                <Badge className="rounded-none text-xs bg-cyan-100 text-cyan-800">Ops Editor</Badge>
                              )}
                              {user.is_ops_pf && (
                                <Badge className="rounded-none text-xs bg-orange-100 text-orange-800">Ops PF</Badge>
                              )}
                              {user.is_finance && (
                                <Badge className="rounded-none text-xs bg-green-100 text-green-800" data-testid={`finance-badge-${user.id}`}>Finance</Badge>
                              )}
                              {user.roles?.map((role, i) => (
                                <Badge key={i} variant="outline" className="rounded-none text-xs uppercase">
                                  {role}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex flex-col gap-1 max-w-[200px]">
                              {user.organizations?.slice(0, 2).map((org, i) => (
                                <span key={i} className="text-xs truncate" title={org.name}>
                                  {org.name}
                                </span>
                              ))}
                              {user.organizations?.length > 2 && (
                                <span className="text-xs text-muted-foreground">
                                  +{user.organizations.length - 2} more
                                </span>
                              )}
                              {(!user.organizations || user.organizations.length === 0) && (
                                <span className="text-xs text-muted-foreground">-</span>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge className={`rounded-none text-xs ${getUserStatusBadge(user.status)}`}>
                              {user.status || 'active'}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <Badge className={`rounded-none text-xs ${getLoginStatusBadge(user.login_status)}`}>
                              {user.login_status === 'logged_in' ? 'Logged In' : 'Logged Out'}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Switch
                                data-testid={`ai-agent-flag-toggle-${user.id}`}
                                checked={!!user.ai_agent_access}
                                onCheckedChange={async (checked) => {
                                  try {
                                    await aiAgentApi.toggleFeatureFlag(user.id, checked);
                                    toast.success(`AI Agent ${checked ? 'enabled' : 'disabled'} for ${user.email}`);
                                    fetchAllUsers();
                                  } catch (e) {
                                    toast.error('Failed to update AI Agent flag');
                                  }
                                }}
                              />
                              {user.ai_agent_persona && (
                                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                  {user.ai_agent_persona}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <span className="text-xs">
                              {user.last_login_at ? formatDate(user.last_login_at) : 'Never'}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="text-xs">
                              <p>{formatDate(user.created_at)}</p>
                              <p className="text-muted-foreground">by {user.created_by}</p>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              {(() => {
                                // Owner rows: locked for everyone.
                                if (user.is_owner) {
                                  return <span className="text-xs text-muted-foreground">Owner — protected</span>;
                                }
                                // Super-admin rows: only an Owner can act on them.
                                if (user.is_super_admin && !isOwner()) {
                                  return <span className="text-xs text-muted-foreground">Protected</span>;
                                }
                                return (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 rounded-none text-blue-600"
                                    onClick={() => openEditUser(user)}
                                    title="Edit user / Manage org assignments"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  {user.status === 'active' ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 rounded-none text-yellow-600"
                                      onClick={() => handleUpdateUserStatus(user.id, 'inactive')}
                                      title="Deactivate user"
                                    >
                                      <Power className="h-4 w-4" />
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 rounded-none text-green-600"
                                      onClick={() => handleUpdateUserStatus(user.id, 'active')}
                                      title="Activate user"
                                    >
                                      <Power className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {user.login_status === 'logged_in' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 rounded-none text-orange-600"
                                      onClick={() => handleForceLogout(user.id)}
                                      title="Force logout"
                                    >
                                      <LogOut className="h-4 w-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 rounded-none text-destructive"
                                    onClick={() => handleDeleteUser(user.id, user.name)}
                                    title="Delete user"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Vendor Registry Tab */}
        {activeTab === 'vendor-registry' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Landmark className="h-6 w-6 text-primary" />
                <div>
                  <h2 className="text-xl font-bold">Global Vendor Registry (VRM)</h2>
                  <p className="text-sm text-muted-foreground">Manage utility vendors available to all organizations</p>
                </div>
              </div>
              <Button
                onClick={() => { resetVendorForm(); setShowVendorModal(true); }}
                className="rounded-none"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Vendor
              </Button>
            </div>

            {/* Search */}
            <div className="flex gap-4">
              <div className="flex-1 max-w-md">
                <Input
                  placeholder="Search vendors..."
                  className="rounded-none"
                  value={vendorSearch}
                  onChange={(e) => setVendorSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchVendors()}
                />
              </div>
              <Button variant="outline" className="rounded-none" onClick={fetchVendors} disabled={vendorsLoading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${vendorsLoading ? 'animate-spin' : ''}`} />
                Search
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="border border-border rounded-none p-4 bg-card">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Total Vendors</p>
                <p className="text-3xl font-black tracking-tighter">{vendors.length}</p>
              </div>
              <div className="border border-border rounded-none p-4 bg-card">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Active</p>
                <p className="text-3xl font-black tracking-tighter text-green-600">
                  {vendors.filter(v => v.status === 'active').length}
                </p>
              </div>
              <div className="border border-border rounded-none p-4 bg-card">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">In Use</p>
                <p className="text-3xl font-black tracking-tighter text-blue-600">
                  {vendors.filter(v => v.usage_count > 0).length}
                </p>
              </div>
              <div className="border border-border rounded-none p-4 bg-card">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Pending Review</p>
                <p className="text-3xl font-black tracking-tighter text-orange-600">
                  {reviewQueue.length}
                </p>
              </div>
            </div>

            {/* Review Queue */}
            {reviewQueue.length > 0 && (
              <div className="border border-orange-200 bg-orange-50 rounded-none p-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                  <h3 className="font-medium text-orange-800">Vendors Pending Review ({reviewQueue.length})</h3>
                </div>
                <p className="text-sm text-orange-700 mb-3">
                  These vendors were auto-created from account uploads. Review and approve or merge with existing vendors.
                </p>
                <div className="bg-white border border-orange-200 rounded-none overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-orange-100">
                      <tr>
                        <th className="text-left p-2 font-medium">Vendor Name</th>
                        <th className="text-left p-2 font-medium">Code</th>
                        <th className="text-left p-2 font-medium">Created From</th>
                        <th className="text-left p-2 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviewQueue.map(vendor => (
                        <tr key={vendor.id} className="border-t border-orange-100">
                          <td className="p-2 font-medium">{vendor.vendor_name}</td>
                          <td className="p-2 font-mono text-xs">{vendor.vendor_code || '-'}</td>
                          <td className="p-2 text-xs text-muted-foreground">Account Upload</td>
                          <td className="p-2">
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-none h-7 text-green-600 border-green-300 hover:bg-green-50"
                                onClick={() => handleReviewAction(vendor.id, 'approve')}
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-none h-7"
                                onClick={() => { setMergeVendor(vendor); setShowMergeModal(true); }}
                              >
                                Merge
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-none h-7 text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => handleReviewAction(vendor.id, 'reject')}
                              >
                                Reject
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Table */}
            <div className="border border-border rounded-none bg-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Vendor</th>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Contact</th>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Commodities</th>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Payment Methods</th>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Org Usage</th>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Status</th>
                      <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendorsLoading ? (
                      <tr>
                        <td colSpan={7} className="text-center p-8 text-muted-foreground">
                          <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                          Loading vendors...
                        </td>
                      </tr>
                    ) : vendors.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center p-8 text-muted-foreground">
                          <Landmark className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>No vendors found</p>
                          <p className="text-xs mt-1">Add your first vendor to get started</p>
                        </td>
                      </tr>
                    ) : (
                      vendors.map((vendor) => (
                        <tr key={vendor.id} className="border-b border-border hover:bg-muted/30">
                          <td className="p-3">
                            <div>
                              <p className="font-medium">{vendor.vendor_name}</p>
                              {vendor.vendor_code && (
                                <p className="text-xs text-muted-foreground font-mono">{vendor.vendor_code}</p>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="text-sm">
                              {vendor.email && <p>{vendor.email}</p>}
                              {vendor.support_phone && <p className="text-muted-foreground">{vendor.support_phone}</p>}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex flex-wrap gap-1">
                              {(vendor.commodities_served || []).map(c => (
                                <Badge key={c} variant="outline" className="rounded-none text-xs capitalize">
                                  {c.replace('_', ' ')}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(vendor.payment_methods || {})
                                .filter(([_, v]) => v?.enabled)
                                .map(([k]) => (
                                  <Badge key={k} variant="secondary" className="rounded-none text-xs uppercase">
                                    {k.replace('_', ' ')}
                                  </Badge>
                                ))}
                            </div>
                          </td>
                          <td className="p-3">
                            <span className={`text-sm font-medium ${vendor.usage_count > 0 ? 'text-blue-600' : 'text-muted-foreground'}`}>
                              {vendor.usage_count || 0} org(s)
                            </span>
                          </td>
                          <td className="p-3">
                            <Badge className={`rounded-none text-xs ${vendor.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                              {vendor.status || 'active'}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 rounded-none"
                                onClick={() => openEditVendor(vendor)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 rounded-none text-destructive hover:text-destructive"
                                onClick={() => { setEditingVendor(vendor); setShowDeleteVendor(true); }}
                                disabled={vendor.usage_count > 0}
                                title={vendor.usage_count > 0 ? 'Cannot delete vendor in use' : 'Delete'}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Payment Files Engine Tab */}
        {activeTab === 'payment-files' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard className="h-6 w-6 text-primary" />
                <div>
                  <h2 className="text-xl font-bold">Payment Files Engine</h2>
                  <p className="text-sm text-muted-foreground">Pull and manage payment files from BillPortal Engine</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={duplicateAction} onValueChange={setDuplicateAction}>
                  <SelectTrigger className="w-36 rounded-none">
                    <SelectValue placeholder="Duplicate action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="skip">Skip duplicates</SelectItem>
                    <SelectItem value="overwrite">Overwrite</SelectItem>
                    <SelectItem value="keep_both">Keep both</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={handlePullFromEngine}
                  disabled={pfePulling || pfeCustomerConfigs.length === 0}
                  className="rounded-none"
                >
                  {pfePulling ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Pull from Engine
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="border border-border rounded-none p-4 bg-card">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Total Pulled</p>
                <p className="text-3xl font-black tracking-tighter">{pfeStats.total}</p>
              </div>
              <div className="border border-border rounded-none p-4 bg-card">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Pending Review</p>
                <p className="text-3xl font-black tracking-tighter text-orange-600">{pfeStats.pending_review}</p>
              </div>
              <div className="border border-border rounded-none p-4 bg-card">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Assigned</p>
                <p className="text-3xl font-black tracking-tighter text-blue-600">{pfeStats.assigned}</p>
              </div>
              <div className="border border-border rounded-none p-4 bg-card">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Published</p>
                <p className="text-3xl font-black tracking-tighter text-green-600">{pfeStats.published}</p>
              </div>
            </div>

            {/* Customer Configurations Section */}
            <div className="border border-border rounded-none bg-card p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-muted-foreground" />
                  <h3 className="font-semibold">Engine Customer Configurations</h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-none"
                  onClick={() => setShowAddCustomerConfig(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Customer
                </Button>
              </div>
              
              {pfeCustomerConfigs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No customer configurations. Add a customer ID to start pulling payment files.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {pfeCustomerConfigs.map(config => (
                    <div key={config.customer_id} className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-none border">
                      <span className="text-sm font-medium">{config.customer_name}</span>
                      <span className="text-xs text-muted-foreground font-mono">{config.customer_id.slice(0, 8)}...</span>
                      {config.auto_assign_account_name && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-none" data-testid={`auto-assign-badge-${config.customer_id}`}>
                          Auto: {config.auto_assign_account_name}
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteCustomerConfig(config.customer_id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Preview Section */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Enter Customer ID to preview..."
                    className="flex-1 rounded-none max-w-md"
                    value={previewCustomerId}
                    onChange={(e) => setPreviewCustomerId(e.target.value)}
                  />
                  <Button
                    variant="outline"
                    className="rounded-none"
                    onClick={handlePreviewEngineFiles}
                    disabled={pfePreviewLoading}
                  >
                    {pfePreviewLoading ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Eye className="h-4 w-4 mr-2" />
                    )}
                    Preview Files
                  </Button>
                </div>
              </div>
            </div>

            {/* Tabs: Unassigned | Unpublished | Published */}
            <div className="border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex gap-0">
                  {[
                    { key: 'unassigned', label: 'Unassigned', count: pfeStats.pending_review },
                    { key: 'unpublished', label: 'Unpublished', count: pfeStats.assigned },
                    { key: 'published', label: 'Published', count: pfeStats.published },
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => { setPfeTab(tab.key); setPfeSelectedFiles(new Set()); }}
                      className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                        pfeTab === tab.key
                          ? 'border-primary text-primary'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`pfe-tab-${tab.key}`}
                    >
                      {tab.label}
                      <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                        pfeTab === tab.key ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                      }`}>{tab.count}</span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 pb-1">
                  {/* Org Filter */}
                  <Select value={pfeOrgFilter} onValueChange={setPfeOrgFilter}>
                    <SelectTrigger className="w-48 rounded-none h-8 text-xs" data-testid="pfe-org-filter">
                      <SelectValue placeholder="Filter by Org" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Orgs</SelectItem>
                      {accounts.map(acc => (
                        <SelectItem key={acc.id} value={acc.id}>{acc.organisation_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" className="rounded-none h-8 text-xs" onClick={fetchPfeFiles} disabled={pfeFilesLoading}>
                    <RefreshCw className={`h-3 w-3 mr-1 ${pfeFilesLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>
            </div>

            {/* Bulk Actions Bar (for Unpublished tab) */}
            {pfeTab === 'unpublished' && (
              <div className="flex items-center justify-between bg-muted/50 border border-border rounded-none px-4 py-2">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">
                    {pfeSelectedFiles.size > 0 ? `${pfeSelectedFiles.size} selected` : 'Select files to bulk publish'}
                  </span>
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={pfeUseOriginalAmounts}
                      onChange={(e) => setPfeUseOriginalAmounts(e.target.checked)}
                      className="rounded-none"
                      data-testid="use-original-amounts-checkbox"
                    />
                    Use Original Requested Amounts
                  </label>
                </div>
                <Button
                  size="sm"
                  className="rounded-none"
                  disabled={pfeSelectedFiles.size === 0 || pfeBulkPublishing}
                  onClick={handleBulkPublish}
                  data-testid="bulk-publish-btn"
                >
                  {pfeBulkPublishing ? (
                    <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Upload className="h-3 w-3 mr-1" />
                  )}
                  Bulk Publish ({pfeSelectedFiles.size})
                </Button>
              </div>
            )}

            {/* Files Table */}
            <div className="border border-border rounded-none overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted border-b border-border">
                  <tr>
                    {pfeTab === 'unpublished' && (
                      <th className="px-3 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={pfeSelectedFiles.size > 0 && pfeSelectedFiles.size === pfeFiles.filter(f => f.local_status === 'assigned').length}
                          onChange={() => toggleSelectAll(pfeFiles)}
                          data-testid="pfe-select-all"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wide font-semibold">Batch Name</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wide font-semibold">Org</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wide font-semibold">Engine Status</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wide font-semibold">Pulled At</th>
                    <th className="px-4 py-3 text-right text-xs uppercase tracking-wide font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {pfeFilesLoading ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                      </td>
                    </tr>
                  ) : (() => {
                    // Filter files based on active tab and org filter
                    const tabStatusMap = { unassigned: 'pending_review', unpublished: 'assigned', published: 'published' };
                    const tabFiles = pfeFiles.filter(f => {
                      const statusMatch = f.local_status === tabStatusMap[pfeTab];
                      const orgMatch = pfeOrgFilter === 'all' || f.assigned_account_id === pfeOrgFilter;
                      return statusMatch && orgMatch;
                    });
                    
                    if (tabFiles.length === 0) {
                      return (
                        <tr>
                          <td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">
                            No files in this tab.
                          </td>
                        </tr>
                      );
                    }
                    
                    return tabFiles.map((file) => (
                      <tr key={file.external_file_id} className={`hover:bg-muted/30 ${pfeSelectedFiles.has(file.external_file_id) ? 'bg-primary/5' : ''}`}>
                        {pfeTab === 'unpublished' && (
                          <td className="px-3 py-3">
                            <input
                              type="checkbox"
                              checked={pfeSelectedFiles.has(file.external_file_id)}
                              onChange={() => toggleFileSelection(file.external_file_id)}
                              data-testid={`pfe-select-${file.external_file_id}`}
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div>
                            <span className="font-medium">{file.batch_name}</span>
                            <p className="text-xs text-muted-foreground font-mono">{file.external_file_id.slice(0, 12)}...</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {file.assigned_account_name || (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={
                            file.engine_status === 'Paid' ? 'default' :
                            file.engine_status === 'Approved' ? 'secondary' :
                            file.engine_status === 'Review' ? 'outline' : 'outline'
                          } className={`rounded-none ${
                            file.engine_status === 'Paid' ? 'bg-green-600' :
                            file.engine_status === 'Approved' ? 'bg-blue-600' : ''
                          }`}>
                            {file.engine_status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {file.engine_generated_at ? new Date(file.engine_generated_at).toLocaleDateString() : file.pulled_at ? new Date(file.pulled_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleDownloadEngineCsv(file)}
                              title="Download CSV from Engine"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {file.local_status === 'pending_review' && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-none"
                                onClick={() => {
                                  setAssigningFile({ ...file, selectedAccountId: '' });
                                  setShowAssignDialog(true);
                                }}
                              >
                                Assign
                              </Button>
                            )}
                            {file.local_status === 'assigned' && (
                              <Button
                                size="sm"
                                className="rounded-none"
                                onClick={() => handlePublishFile(file)}
                              >
                                Publish
                              </Button>
                            )}
                            {file.local_status !== 'published' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive"
                                onClick={() => handleDeletePfeFile(file.external_file_id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                            {file.local_status === 'published' && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-none text-orange-600 border-orange-300 hover:bg-orange-50"
                                onClick={() => handleUnpublishFile(file)}
                                data-testid="unpublish-file-btn"
                              >
                                <RotateCcw className="h-3 w-3 mr-1" />
                                Unpublish
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Email Config Tab */}
        {activeTab === 'email' && (
          <div className="max-w-2xl">
            <div className="border border-border rounded-none bg-card p-6">
              <div className="flex items-center gap-3 mb-6">
                <Mail className="h-6 w-6 text-primary" />
                <div>
                  <h2 className="text-xl font-bold">Email Service Configuration</h2>
                  <p className="text-sm text-muted-foreground">Configure platform-wide email service for inbound bill processing</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Email Service Provider</Label>
                  <Select
                    value={emailConfig.provider}
                    onValueChange={(value) => setEmailConfig({ ...emailConfig, provider: value })}
                  >
                    <SelectTrigger className="rounded-none mt-1">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not Configured</SelectItem>
                      <SelectItem value="sendgrid">SendGrid</SelectItem>
                      <SelectItem value="mailgun">Mailgun</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {emailConfig.provider !== 'none' && (
                  <>
                    <div>
                      <Label>API Key</Label>
                      <Input
                        type="password"
                        className="rounded-none mt-1 font-mono"
                        value={emailConfig.api_key}
                        onChange={(e) => setEmailConfig({ ...emailConfig, api_key: e.target.value })}
                        placeholder={`Enter ${emailConfig.provider} API key`}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {emailConfig.provider === 'sendgrid' 
                          ? 'Get your API key from SendGrid Dashboard → Settings → API Keys'
                          : 'Get your API key from Mailgun Dashboard → API Security → API Keys'}
                      </p>
                    </div>

                    {emailConfig.provider === 'mailgun' && (
                      <div>
                        <Label>Webhook Signing Key (Optional)</Label>
                        <Input
                          type="password"
                          className="rounded-none mt-1 font-mono"
                          value={emailConfig.webhook_signing_key}
                          onChange={(e) => setEmailConfig({ ...emailConfig, webhook_signing_key: e.target.value })}
                          placeholder="Enter Mailgun webhook signing key"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Found in Mailgun Dashboard → Sending → Webhooks → Webhook signing key. 
                          Used to verify inbound emails are from Mailgun.
                        </p>
                      </div>
                    )}

                    <div>
                      <Label>Inbound Domain</Label>
                      <Input
                        className="rounded-none mt-1 font-mono"
                        value={emailConfig.domain}
                        onChange={(e) => setEmailConfig({ ...emailConfig, domain: e.target.value })}
                        placeholder="inbound.billportal.io"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Domain used for receiving emails (e.g., orgname.123@{emailConfig.domain})
                      </p>
                    </div>

                    <div className="bg-muted/30 p-4 rounded-none">
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Setup Instructions
                      </h4>
                      <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                        {emailConfig.provider === 'sendgrid' ? (
                          <>
                            <li>Go to SendGrid Dashboard → Settings → Inbound Parse</li>
                            <li>Click &quot;Add Host&quot; and enter your receiving domain</li>
                            <li>Set webhook URL to: <code className="bg-muted px-1">{window.location.origin}/api/inbound-email/webhook</code></li>
                            <li>Enable &quot;Spam Check&quot; (optional)</li>
                            <li>Add MX records for your domain pointing to mx.sendgrid.net</li>
                          </>
                        ) : (
                          <>
                            <li>Go to Mailgun Dashboard → Sending → Domains</li>
                            <li>Add your receiving domain (e.g., inbound.billportal.io)</li>
                            <li>Add MX records: <code className="bg-muted px-1">mxa.mailgun.org</code> and <code className="bg-muted px-1">mxb.mailgun.org</code></li>
                            <li>Go to Sending → Routes and create a new Route</li>
                            <li>Set filter: <code className="bg-muted px-1">match_recipient(&quot;.*@{emailConfig.domain}&quot;)</code></li>
                            <li>Set action: <code className="bg-muted px-1">forward(&quot;{window.location.origin}/api/inbound-email/webhook&quot;)</code></li>
                          </>
                        )}
                      </ol>
                    </div>
                  </>
                )}

                <div className="flex items-center gap-3 pt-4">
                  <Button
                    onClick={handleSaveEmailConfig}
                    disabled={savingEmail}
                    className="rounded-none"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {savingEmail ? 'Saving...' : 'Save Email Config'}
                  </Button>
                  {emailConfig.is_configured && (
                    <div className="flex items-center gap-1 text-green-600 text-sm">
                      <CheckCircle className="h-4 w-4" />
                      Configured
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Support Email Tab */}
        {activeTab === 'support-email' && (
          <div className="max-w-4xl space-y-6" data-testid="support-email-tab">
            <div className="border border-border rounded-none bg-card p-6">
              <div className="flex items-center gap-3 mb-6">
                <LifeBuoy className="h-6 w-6 text-primary" />
                <div>
                  <h2 className="text-xl font-bold">Support Email</h2>
                  <p className="text-sm text-muted-foreground">
                    Onboarding call requests raised via the AI Agent land in this inbox, with the requesting user CC'd and their company details included.
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Support inbox address</Label>
                  <Input
                    type="email"
                    value={supportConfig.support_email}
                    onChange={(e) => setSupportConfig({ ...supportConfig, support_email: e.target.value })}
                    placeholder="support@billportal.io"
                    className="rounded-none mt-1"
                    data-testid="support-email-input"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={supportConfig.cc_user}
                    onChange={(e) => setSupportConfig({ ...supportConfig, cc_user: e.target.checked })}
                    data-testid="support-cc-user-checkbox"
                  />
                  CC the requesting user on the email
                </label>
                <div className="flex items-center gap-4 pt-2">
                  <Button onClick={handleSaveSupportConfig} disabled={savingSupport} className="rounded-none" data-testid="save-support-config-btn">
                    <Save className="h-4 w-4 mr-2" />
                    {savingSupport ? 'Saving...' : 'Save Support Config'}
                  </Button>
                </div>
              </div>
            </div>

            <div className="border border-border rounded-none bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-bold">Call Requests</h3>
                </div>
                <Button variant="outline" size="sm" className="rounded-none" onClick={fetchSupportRequests} data-testid="refresh-support-requests-btn">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
              {loadingSupportRequests ? (
                <div className="py-8 text-center text-muted-foreground text-sm">Loading…</div>
              ) : supportRequests.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground text-sm" data-testid="support-requests-empty">
                  No call requests yet. When a user asks Astrid to schedule a call, it appears here.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="support-requests-table">
                    <thead>
                      <tr className="border-b border-border text-left text-muted-foreground">
                        <th className="py-2 pr-4">Requested</th>
                        <th className="py-2 pr-4">Company</th>
                        <th className="py-2 pr-4">User</th>
                        <th className="py-2 pr-4">Preferred times</th>
                        <th className="py-2 pr-4">Topic</th>
                        <th className="py-2 pr-4">Email</th>
                        <th className="py-2 pr-4">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supportRequests.map((r) => (
                        <tr key={r.id} className="border-b border-border/50 align-top">
                          <td className="py-2 pr-4 whitespace-nowrap">{(r.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                          <td className="py-2 pr-4">
                            <div className="font-medium">{r.org_name || '—'}</div>
                            <div className="text-xs text-muted-foreground">{r.property_count || 0} properties</div>
                          </td>
                          <td className="py-2 pr-4">
                            <div>{r.user_name || '—'}</div>
                            <div className="text-xs text-muted-foreground">{r.user_email}</div>
                            {r.contact_phone && <div className="text-xs text-muted-foreground">{r.contact_phone}</div>}
                          </td>
                          <td className="py-2 pr-4 max-w-[180px]">{r.preferred_times}</td>
                          <td className="py-2 pr-4 max-w-[180px]">
                            <div>{r.topic}</div>
                            {r.notes && <div className="text-xs text-muted-foreground">{r.notes}</div>}
                          </td>
                          <td className="py-2 pr-4">
                            {r.email_status === 'sent' ? (
                              <span className="text-green-600 text-xs flex items-center gap-1"><CheckCircle className="h-3 w-3" /> sent</span>
                            ) : r.email_status === 'skipped_no_provider' ? (
                              <span className="text-amber-600 text-xs">no provider</span>
                            ) : (
                              <span className="text-red-600 text-xs">{r.email_status}</span>
                            )}
                          </td>
                          <td className="py-2 pr-4">
                            <select
                              value={r.status}
                              onChange={(e) => updateSupportRequestStatus(r.id, e.target.value)}
                              className="border border-border bg-background text-xs px-1 py-0.5"
                              data-testid={`support-request-status-${r.id}`}
                            >
                              <option value="open">open</option>
                              <option value="contacted">contacted</option>
                              <option value="closed">closed</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Registration Configuration Tab */}
        {activeTab === 'registration' && <RegistrationConfigTab />}
        {/* Support Requests Tab */}
        {activeTab === 'support-requests' && <SupportRequestsTab />}

        {/* Social Sign-in (OAuth) Configuration Tab */}
        {activeTab === 'oauth' && <OAuthConfigTab />}

        {/* Subscription Pricing Tab */}
        {activeTab === 'sub-pricing' && <SubscriptionPricingTab />}

        {/* Add Credits Tab */}
        {activeTab === 'add-credits' && <AddCreditsTab />}

        {/* Credit Top-up Report Tab */}
        {activeTab === 'topup-report' && <CreditTopupReportTab />}
        {activeTab === 'billing-history' && <BillingHistoryTab />}

        {activeTab === 'crm-sync' && <CrmSyncTab />}
        {activeTab === 'backend-crm' && <BackendCrmTab />}

        {activeTab === 'emission-factors' && <EmissionFactorsTab />}
        {activeTab === 'stripe-config' && isOwner() && <StripeConfigTab />}

        {activeTab === 'sa_finance_report' && <FinanceReportTab />}

        {activeTab === 'sa_finance_refunds' && <FinanceRefundsTab />}

        {activeTab === 'finance-invoices' && <BillingInvoicesTab />}

        {/* AI Model Setup Tab */}
        {activeTab === 'ai' && (
          <div className="max-w-2xl">
            <div className="border border-border rounded-none bg-card p-6">
              <div className="flex items-center gap-3 mb-6">
                <Bot className="h-6 w-6 text-primary" />
                <div>
                  <h2 className="text-xl font-bold">AI Model Configuration</h2>
                  <p className="text-sm text-muted-foreground">Configure the default AI model for bill data extraction</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>AI Provider</Label>
                  <Select
                    value={aiConfig.provider}
                    onValueChange={(value) => setAiConfig({ 
                      ...aiConfig, 
                      provider: value, 
                      model_name: providerModels[value]?.[0] || 'gpt-5.2'
                    })}
                  >
                    <SelectTrigger className="rounded-none mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="emergent">Emergent (Universal Key)</SelectItem>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="anthropic">Anthropic</SelectItem>
                      <SelectItem value="gemini">Google Gemini</SelectItem>
                    </SelectContent>
                  </Select>
                  {aiConfig.provider === 'emergent' && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Using Emergent Universal Key - no additional API key required
                    </p>
                  )}
                </div>

                {aiConfig.provider !== 'emergent' && (
                  <div>
                    <Label>API Key</Label>
                    <Input
                      type="password"
                      className="rounded-none mt-1 font-mono"
                      value={aiConfig.api_key}
                      onChange={(e) => setAiConfig({ ...aiConfig, api_key: e.target.value })}
                      placeholder={`Enter ${aiConfig.provider} API key`}
                    />
                  </div>
                )}

                <div>
                  <Label>Model</Label>
                  <Select
                    value={aiConfig.model_name}
                    onValueChange={(value) => setAiConfig({ ...aiConfig, model_name: value })}
                  >
                    <SelectTrigger className="rounded-none mt-1" data-testid="ai-model-name-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(providerModels[aiConfig.provider] || providerModels.emergent).map((model) => (
                        <SelectItem key={model} value={model} data-testid={`ai-model-option-${model}`}>{model}</SelectItem>
                      ))}
                      {/* If a previously-saved model isn't in the canonical
                          list (e.g. a stale typo like `claude-sonnet-4-20250514`),
                          surface it as a disabled-style option flagged
                          "(invalid — pick a canonical model above)" so the
                          user can SEE what's currently broken instead of
                          a silently blank Select. */}
                      {aiConfig.model_name &&
                        !(providerModels[aiConfig.provider] || providerModels.emergent).includes(aiConfig.model_name) && (
                        <SelectItem
                          key={aiConfig.model_name}
                          value={aiConfig.model_name}
                          data-testid="ai-model-option-stale"
                        >
                          {aiConfig.model_name} (stale — pick a current model)
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Only canonical 2026 model names are accepted. Typos
                    will cause the parser to fail loudly (HTTP 404).
                  </p>
                </div>

                <div className="flex items-center gap-3 pt-4">
                  <Button
                    onClick={handleSaveAiConfig}
                    disabled={savingAi}
                    className="rounded-none"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {savingAi ? 'Saving...' : 'Save AI Config'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Database Migration Section */}
            <div className="border border-border rounded-none bg-card p-6 mt-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Database className="h-5 w-5 text-primary" />
                  <div>
                    <h3 className="font-semibold">Database Migration</h3>
                    <p className="text-sm text-muted-foreground">
                      Generate a temporary API key for database export/migration
                    </p>
                  </div>
                </div>
                <Button
                  onClick={generateMigrationKey}
                  disabled={migrationKeyLoading}
                  className="rounded-none"
                  data-testid="generate-migration-key-btn"
                >
                  <Key className={`h-4 w-4 mr-2 ${migrationKeyLoading ? 'animate-spin' : ''}`} />
                  {migrationKeyLoading ? 'Generating...' : 'Generate Migration Key'}
                </Button>
              </div>

              {migrationKey && (
                <div className="space-y-4">
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-800 dark:text-green-200 font-medium mb-1">Migration Key Generated</p>
                        <p className="text-xs text-green-600 dark:text-green-400">Expires: {new Date(migrationKey.expires_at).toLocaleString()}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        onClick={() => copyToClipboard(migrationKey.key)}
                        data-testid="copy-migration-key-btn"
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Copy Key
                      </Button>
                    </div>
                    <div className="mt-3">
                      <code className="block p-3 bg-white dark:bg-black/30 text-sm font-mono break-all border">
                        {migrationKey.key}
                      </code>
                    </div>
                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-2 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Save this key now - it won't be shown again!
                    </p>
                  </div>

                  <div className="bg-muted/30 border border-border p-4">
                    <h4 className="font-semibold text-sm mb-2">How to use:</h4>
                    <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                      <li>Share this key with the migration tool/agent</li>
                      <li>
                        Export API endpoint:
                        <pre className="mt-1 p-2 bg-muted text-xs font-mono overflow-x-auto">
                          GET /api/migration/export?collection=COLLECTION_NAME
                        </pre>
                      </li>
                      <li>
                        Use header:
                        <pre className="mt-1 p-2 bg-muted text-xs font-mono">
                          X-Migration-Key: {migrationKey.key.substring(0, 20)}...
                        </pre>
                      </li>
                    </ol>
                  </div>
                </div>
              )}

              {!migrationKey && (
                <div className="text-center py-6 text-muted-foreground">
                  <Database className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Generate a migration key to enable database export</p>
                  <p className="text-xs mt-1">Key expires after 24 hours for security</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Outbound IP Config Tab */}
        {activeTab === 'outbound-ip' && (
          <div className="max-w-3xl">
            <div className="border border-border rounded-none bg-card p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Network className="h-6 w-6 text-primary" />
                  <div>
                    <h2 className="text-xl font-bold">Outbound IP Configuration</h2>
                    <p className="text-sm text-muted-foreground">
                      Check the outbound IP address for external database whitelisting
                    </p>
                  </div>
                </div>
                <Button
                  onClick={fetchOutboundIp}
                  disabled={outboundIpLoading}
                  className="rounded-none"
                  data-testid="refresh-outbound-ip-btn"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${outboundIpLoading ? 'animate-spin' : ''}`} />
                  {outboundIpLoading ? 'Checking...' : 'Refresh IP'}
                </Button>
              </div>

              {outboundIpData && (
                <div className="space-y-6">
                  {/* Consensus IP Card */}
                  <div className="bg-muted/50 border border-border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Detected Outbound IP</p>
                        <p className="text-2xl font-mono font-bold" data-testid="consensus-ip">
                          {outboundIpData.consensus_ip || 'Unable to detect'}
                        </p>
                      </div>
                      {outboundIpData.consensus_ip && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-none"
                          onClick={() => copyToClipboard(outboundIpData.consensus_ip)}
                          data-testid="copy-ip-btn"
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Copy IP
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Use this IP address to whitelist in your external PostgreSQL pg_hba.conf
                    </p>
                  </div>

                  {/* Individual Service Results */}
                  <div>
                    <h3 className="text-sm font-semibold mb-3">IP Detection Services</h3>
                    <div className="space-y-2">
                      {outboundIpData.results.map((result, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 border border-border bg-card"
                          data-testid={`ip-service-${result.service}`}
                        >
                          <div className="flex items-center gap-3">
                            <Globe className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="font-medium text-sm">{result.service}</p>
                              <p className="text-xs text-muted-foreground font-mono">{result.url}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {result.status === 'success' ? (
                              <>
                                <Badge 
                                  variant="default" 
                                  className="rounded-none font-mono text-xs px-2 py-1"
                                  data-testid={`ip-badge-${result.service}`}
                                >
                                  {result.ip}
                                </Badge>
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              </>
                            ) : (
                              <>
                                <Badge 
                                  variant="destructive" 
                                  className="rounded-none text-xs px-2 py-1"
                                >
                                  Error
                                </Badge>
                                <XCircle className="h-4 w-4 text-red-500" />
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Last Checked */}
                  <div className="text-xs text-muted-foreground text-right">
                    Last checked: {new Date(outboundIpData.checked_at).toLocaleString()}
                  </div>

                  {/* PostgreSQL Whitelisting Instructions */}
                  <div className="bg-muted/30 border border-border p-4">
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      PostgreSQL Whitelisting Instructions
                    </h4>
                    <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                      <li>
                        Add the following line to your <code className="bg-muted px-1 rounded">pg_hba.conf</code>:
                        <pre className="mt-1 p-2 bg-muted text-xs font-mono overflow-x-auto">
                          host    bpanalytics    satish_readonly    {outboundIpData.consensus_ip || '0.0.0.0'}/32    md5
                        </pre>
                      </li>
                      <li>
                        Reload PostgreSQL configuration:
                        <pre className="mt-1 p-2 bg-muted text-xs font-mono">
                          sudo systemctl reload postgresql
                        </pre>
                      </li>
                      <li>
                        Verify the connection works by testing the Payment Verification module
                      </li>
                    </ol>
                  </div>
                </div>
              )}

              {!outboundIpData && !outboundIpLoading && (
                <div className="text-center py-8 text-muted-foreground">
                  <Network className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Click "Refresh IP" to check the outbound IP address</p>
                </div>
              )}

              {outboundIpLoading && !outboundIpData && (
                <div className="text-center py-8 text-muted-foreground">
                  <RefreshCw className="h-12 w-12 mx-auto mb-4 animate-spin opacity-30" />
                  <p>Checking outbound IP...</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Report: Bill Stream */}
        {activeTab === 'report-bill-stream' && (
          <BillStreamReport />
        )}

        {/* Invoices Module */}
        {activeTab === 'invoices' && (
          <InvoicesPage />
        )}

        {/* Report: Missing Bills (placeholder) */}
        {activeTab === 'report-missing-bills' && (
          <div className="border border-border rounded-none bg-card p-8">
            <div className="text-center text-muted-foreground">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <h3 className="text-lg font-semibold mb-2">Missing Bills Report</h3>
              <p className="text-sm">Coming soon</p>
            </div>
          </div>
        )}

        {/* Report: Customer Report */}
        {activeTab === 'report-customer' && (
          <div className="space-y-6" data-testid="customer-report">
            <div className="border border-border rounded-none bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Building2 className="h-6 w-6 text-primary" />
                  <div>
                    <h2 className="text-xl font-bold">Customer Report</h2>
                    <p className="text-sm text-muted-foreground">Properties, Units, Meters, and Vendor Accounts by customer</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="rounded-none" onClick={fetchCustomerReport} disabled={customerReportLoading} data-testid="refresh-customer-report">
                  <RefreshCw className={`h-4 w-4 mr-1 ${customerReportLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-muted/30 border border-border p-3 text-center">
                  <p className="text-2xl font-bold font-mono">{customerReport.length}</p>
                  <p className="text-xs text-muted-foreground">Customers</p>
                </div>
                <div className="bg-muted/30 border border-border p-3 text-center">
                  <p className="text-2xl font-bold font-mono">
                    <span className="text-blue-600">{customerReport.reduce((s, c) => s + (c.properties || 0), 0)}</span>
                    <span className="text-muted-foreground mx-1">/</span>
                    <span>{customerReport.reduce((s, c) => s + (c.mysql?.properties || 0), 0)}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">Properties (Synced / Total)</p>
                </div>
                <div className="bg-muted/30 border border-border p-3 text-center">
                  <p className="text-2xl font-bold font-mono">
                    <span className="text-blue-600">{customerReport.reduce((s, c) => s + (c.spaces || 0), 0)}</span>
                    <span className="text-muted-foreground mx-1">/</span>
                    <span>{customerReport.reduce((s, c) => s + (c.mysql?.spaces || 0), 0)}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">Units (Synced / Total)</p>
                </div>
                <div className="bg-muted/30 border border-border p-3 text-center">
                  <p className="text-2xl font-bold font-mono">
                    <span className="text-blue-600">{customerReport.reduce((s, c) => s + (c.utility_accounts || 0), 0)}</span>
                    <span className="text-muted-foreground mx-1">/</span>
                    <span>{customerReport.reduce((s, c) => s + (c.mysql?.utility_accounts || 0), 0)}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">Utility Accounts (Synced / Total)</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Customer</th>
                      <th className="text-right p-3 font-medium text-xs uppercase tracking-wide">Properties (Sync)</th>
                      <th className="text-right p-3 font-medium text-xs uppercase tracking-wide">Units (Sync)</th>
                      <th className="text-right p-3 font-medium text-xs uppercase tracking-wide">Utility Accounts (Sync)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerReportLoading ? (
                      <tr>
                        <td colSpan={4} className="text-center p-8 text-muted-foreground">
                          <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
                          Loading...
                        </td>
                      </tr>
                    ) : customerReport.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center p-6 text-muted-foreground text-sm">No customer data available</td>
                      </tr>
                    ) : (
                      customerReport.map((cust) => {
                        const mysql = cust.mysql || {};
                        const syncCell = (synced, total) => {
                          const pct = total > 0 ? Math.round((synced / total) * 100) : 0;
                          const isFull = synced >= total && total > 0;
                          return (
                            <>
                              <span className={isFull ? 'text-emerald-600' : synced > 0 ? 'text-blue-600' : 'text-muted-foreground'}>{synced.toLocaleString()}</span>
                              <span className="text-muted-foreground mx-0.5">/</span>
                              <span className="font-medium">{total.toLocaleString()}</span>
                              {total > 0 && (
                                <span className={`ml-1.5 text-[10px] ${isFull ? 'text-emerald-600' : 'text-amber-600'}`}>
                                  {pct}%
                                </span>
                              )}
                            </>
                          );
                        };
                        return (
                          <tr key={cust.account_id} className="border-t border-border hover:bg-muted/30">
                            <td className="p-3 font-medium">{cust.organisation_name}</td>
                            <td className="p-3 text-right font-mono text-sm">{syncCell(cust.properties || 0, mysql.properties || 0)}</td>
                            <td className="p-3 text-right font-mono text-sm">{syncCell(cust.spaces || 0, mysql.spaces || 0)}</td>
                            <td className="p-3 text-right font-mono text-sm">{syncCell(cust.utility_accounts || 0, mysql.utility_accounts || 0)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Report: Account & Bill Summary */}
        {activeTab === 'report-account-bill' && (
          <div className="space-y-6" data-testid="account-bill-report-tab">
            <AccountBillReport />
          </div>
        )}

        {/* MySQL/MongoDB Browser */}
        {activeTab === 'mysql-browser' && (
          <div data-testid="mysql-browser-tab">
            <DbBrowser />
          </div>
        )}

        {/* Documentation */}
        {activeTab === 'documentation' && (
          <div data-testid="documentation-tab">
            <DocumentationPage />
          </div>
        )}

        {/* Regression Tests */}
        {activeTab === 'regression-tests' && (
          <div data-testid="regression-tests-tab">
            <RegressionDashboard />
          </div>
        )}

        {/* Cross-Customer Data Report */}
        {activeTab === 'cross-customer' && (
          <div data-testid="cross-customer-tab">
            <CrossCustomerReportPage />
          </div>
        )}

        {/* Export Reports */}
        {activeTab === 'export-reports' && (
          <ExportReportsTab />
        )}

        {activeTab === 'sa_payments_dashboard' && (
          <SAPaymentsDashboardTab />
        )}

        {activeTab === 'sa_payments_workspace' && (
          <SAPaymentsWorkspaceTab />
        )}

        {activeTab === 'sa_global_credit_debit' && (
          <SAGlobalCreditDebitTab />
        )}

        {activeTab === 'sa_upload_credit_file' && (
          <SAUploadCreditFileTab />
        )}

        {activeTab === 'sa_process_refunds' && (
          <SAProcessRefundsTab accounts={accounts} />
        )}

      </main>
      <Dialog open={showCreateAccount} onOpenChange={setShowCreateAccount}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Create New Organisation</DialogTitle>
            <DialogDescription>Add a new organisation to the system.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAccount}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Organisation Name</Label>
                <Input
                  className="rounded-none mt-1"
                  value={accountForm.organisation_name}
                  onChange={(e) => setAccountForm({ ...accountForm, organisation_name: e.target.value })}
                  placeholder="Enter organisation name"
                  required
                />
              </div>
              <div>
                <Label>Subscription Plan</Label>
                <Select
                  value={accountForm.subscription_plan}
                  onValueChange={(value) => setAccountForm({ ...accountForm, subscription_plan: value })}
                >
                  <SelectTrigger className="rounded-none mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Subscription Start Date</Label>
                  <Input
                    type="date"
                    className="rounded-none mt-1"
                    value={accountForm.subscription_start_date || ''}
                    onChange={(e) => setAccountForm({ ...accountForm, subscription_start_date: e.target.value })}
                    data-testid="create-sub-start"
                  />
                </div>
                <div>
                  <Label>Subscription End Date <span className="text-muted-foreground text-xs">(optional)</span></Label>
                  <Input
                    type="date"
                    className="rounded-none mt-1"
                    value={accountForm.subscription_end_date || ''}
                    onChange={(e) => setAccountForm({ ...accountForm, subscription_end_date: e.target.value })}
                    data-testid="create-sub-end"
                  />
                </div>
              </div>
              <div className="border-t border-border pt-4 mt-4">
                <p className="text-xs text-muted-foreground mb-3 font-medium uppercase tracking-wide">BillPortal Mapping (Optional)</p>
                <div className="space-y-3">
                  <div>
                    <Label>BP Partner</Label>
                    <Select
                      value={accountForm.bp_partner_id ? String(accountForm.bp_partner_id) : 'none'}
                      onValueChange={(value) => setAccountForm({ ...accountForm, bp_partner_id: value === 'none' ? null : parseInt(value), bp_org_id: null })}
                    >
                      <SelectTrigger className="rounded-none mt-1" data-testid="create-bp-partner">
                        <SelectValue placeholder="Select BP Partner" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="none" className="rounded-none">-- None --</SelectItem>
                        {bpPartners.map(p => (
                          <SelectItem key={p.id} value={String(p.id)} className="rounded-none">{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>BP Org</Label>
                    <Select
                      value={accountForm.bp_org_id ? String(accountForm.bp_org_id) : 'none'}
                      onValueChange={(value) => setAccountForm({ ...accountForm, bp_org_id: value === 'none' ? null : parseInt(value) })}
                    >
                      <SelectTrigger className="rounded-none mt-1" data-testid="create-bp-org">
                        <SelectValue placeholder="Select BP Org" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none max-h-60">
                        <SelectItem value="none" className="rounded-none">-- None --</SelectItem>
                        {bpCustomers
                          .filter(c => !accountForm.bp_partner_id || c.partner_id === accountForm.bp_partner_id)
                          .map(c => (
                            <SelectItem key={c.id} value={String(c.id)} className="rounded-none">
                              {c.name} {c.partner_name ? `(${c.partner_name})` : ''}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowCreateAccount(false)}>
                Cancel
              </Button>
              <Button type="submit" className="rounded-none">Create Organisation</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Organisation Dialog with Tabs */}
      <Dialog open={showEditAccount} onOpenChange={setShowEditAccount}>
        <DialogContent className="rounded-none max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Edit Organisation
            </DialogTitle>
            <DialogDescription>Update organisation details, permissions, and workflow configuration.</DialogDescription>
          </DialogHeader>
          
          <Tabs value={editDialogTab} onValueChange={setEditDialogTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 rounded-none">
              <TabsTrigger value="details" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Building2 className="h-4 w-4 mr-2" />
                Org Details
              </TabsTrigger>
              <TabsTrigger value="permissions" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Shield className="h-4 w-4 mr-2" />
                Permissions
              </TabsTrigger>
              <TabsTrigger value="workflow_config" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-workflow-config">
                <ListChecks className="h-4 w-4 mr-2" />
                Workflow Config
              </TabsTrigger>
              <TabsTrigger value="ach" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-ach-config">
                <Landmark className="h-4 w-4 mr-2" />
                ACH Configuration
              </TabsTrigger>
            </TabsList>
            
            {/* Tab 1: Org Details */}
            <TabsContent value="details" className="mt-4">
              <form onSubmit={handleEditAccount}>
                <div className="space-y-4">
                  <div>
                    <Label>Organisation Name</Label>
                    <Input
                      className="rounded-none mt-1"
                      value={editForm.organisation_name}
                      onChange={(e) => setEditForm({ ...editForm, organisation_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Account Number</Label>
                    <Input
                      className="rounded-none mt-1 font-mono"
                      value={editForm.account_no}
                      onChange={(e) => setEditForm({ ...editForm, account_no: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Subscription Plan</Label>
                    <Select
                      value={editForm.subscription_plan}
                      onValueChange={(value) => setEditForm({ ...editForm, subscription_plan: value })}
                    >
                      <SelectTrigger className="rounded-none mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Subscription Start Date</Label>
                      <Input
                        type="date"
                        className="rounded-none mt-1"
                        value={editForm.subscription_start_date || ''}
                        onChange={(e) => setEditForm({ ...editForm, subscription_start_date: e.target.value })}
                        data-testid="edit-sub-start"
                      />
                    </div>
                    <div>
                      <Label>Subscription End Date</Label>
                      <Input
                        type="date"
                        className="rounded-none mt-1"
                        value={editForm.subscription_end_date || ''}
                        onChange={(e) => setEditForm({ ...editForm, subscription_end_date: e.target.value })}
                        data-testid="edit-sub-end"
                      />
                    </div>
                    <div>
                      <Label>Subscription Status</Label>
                      <Select
                        value={editForm.subscription_status || 'open'}
                        onValueChange={(v) => setEditForm({ ...editForm, subscription_status: v })}
                      >
                        <SelectTrigger className="rounded-none mt-1" data-testid="edit-sub-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Auto-derived from end date. Closed = org is inactive.
                      </p>
                    </div>
                  </div>
                  <div>
                    <Label>Missing Bill Start Date <span className="text-muted-foreground text-xs">(optional)</span></Label>
                    <Input
                      type="date"
                      className="rounded-none mt-1"
                      value={editForm.missing_bill_start_date || ''}
                      onChange={(e) => setEditForm({ ...editForm, missing_bill_start_date: e.target.value })}
                      data-testid="edit-missing-bill-start"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Date from which Missing Bills are tracked. Periods before this are historical (hidden from client counts). Defaults to the org creation date.
                    </p>
                  </div>
                  <div className="border-t border-border pt-4 mt-4">
                    <p className="text-xs text-muted-foreground mb-3 font-medium uppercase tracking-wide">BillPortal Mapping</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>BP Partner</Label>
                        <Select
                          value={editForm.bp_partner_id ? String(editForm.bp_partner_id) : 'none'}
                          onValueChange={(value) => setEditForm({ ...editForm, bp_partner_id: value === 'none' ? null : parseInt(value), bp_org_id: null })}
                        >
                          <SelectTrigger className="rounded-none mt-1" data-testid="edit-bp-partner">
                            <SelectValue placeholder="Select BP Partner" />
                          </SelectTrigger>
                          <SelectContent className="rounded-none">
                            <SelectItem value="none" className="rounded-none">-- None --</SelectItem>
                            {bpPartners.map(p => (
                              <SelectItem key={p.id} value={String(p.id)} className="rounded-none">{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>BP Org</Label>
                        <Select
                          value={editForm.bp_org_id ? String(editForm.bp_org_id) : 'none'}
                          onValueChange={(value) => setEditForm({ ...editForm, bp_org_id: value === 'none' ? null : parseInt(value) })}
                        >
                          <SelectTrigger className="rounded-none mt-1" data-testid="edit-bp-org">
                            <SelectValue placeholder="Select BP Org" />
                          </SelectTrigger>
                          <SelectContent className="rounded-none max-h-60">
                            <SelectItem value="none" className="rounded-none">-- None --</SelectItem>
                            {bpCustomers
                              .filter(c => !editForm.bp_partner_id || c.partner_id === editForm.bp_partner_id)
                              .map(c => (
                                <SelectItem key={c.id} value={String(c.id)} className="rounded-none">
                                  {c.name} {c.partner_name ? `(${c.partner_name})` : ''}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowEditAccount(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="rounded-none">Save Details</Button>
                </div>
              </form>
            </TabsContent>
            
            {/* Tab 2: Permissions */}
            <TabsContent value="permissions" className="mt-4">
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Enabled Applications</Label>
                  <p className="text-xs text-muted-foreground mb-3">Select which applications this organisation can access</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'portal_collection', label: 'Portal Collection', icon: FolderOpen, locked: true },
                      { key: 'bill_parsing', label: 'Bill Parsing', icon: FileSearch, locked: true },
                      { key: 'intake_hub', label: 'Intake Hub (Org Access)', icon: FileText, locked: false },
                      { key: 'ubm', label: 'Utility Bill Management', icon: Landmark, locked: false },
                      { key: 'cockpit', label: 'Cockpit (Sync & Jobs)', icon: Gauge, locked: false },
                      { key: 'energy_star', label: 'Energy Star', icon: Star, locked: false },
                      { key: 'sustainability', label: 'Sustainability (Emissions & Targets)', icon: Leaf, locked: false },
                      { key: 'opt', label: 'Operations Planning', icon: ClipboardList, locked: false },
                      { key: 'payments', label: 'Payments', icon: CreditCard, locked: false },
                      { key: 'contracts', label: 'Procurement — Contracts', icon: FileSignature, locked: false },
                      { key: 'budgets', label: 'Procurement — Budgets', icon: Wallet, locked: false },
                      { key: 'analytics', label: 'Analytics', icon: BarChart3, locked: false },
                      { key: 'ai_operations', label: 'AI Operations', icon: Cpu, locked: false },
                      { key: 'paas', label: 'PaaS (Parsing as a Service)', icon: Boxes, locked: false },
                    ].map((app) => {
                      const Icon = app.icon;
                      const isEnabled = editForm.enabled_apps?.[app.key] ?? false;
                      return (
                        <div
                          key={app.key}
                          onClick={() => !app.locked && handleAppToggle(app.key)}
                          className={`flex items-center gap-3 p-3 border rounded-none transition-colors ${
                            app.locked 
                              ? 'bg-muted/50 border-border cursor-not-allowed' 
                              : isEnabled 
                                ? 'bg-primary/5 border-primary cursor-pointer' 
                                : 'border-border hover:border-muted-foreground/50 cursor-pointer'
                          }`}
                        >
                          <div className={`w-5 h-5 border rounded-sm flex items-center justify-center ${
                            isEnabled ? 'bg-primary border-primary' : 'border-muted-foreground/30'
                          }`}>
                            {isEnabled && <Check className="h-3 w-3 text-primary-foreground" />}
                          </div>
                          <Icon className={`h-4 w-4 ${isEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
                          <span className={`text-sm ${isEnabled ? 'font-medium' : 'text-muted-foreground'}`}>
                            {app.label}
                          </span>
                          {app.locked && (
                            <span className="text-[10px] uppercase tracking-wide text-muted-foreground ml-auto">Core</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Privacy — display-only masking */}
                <div className="border-t pt-4">
                  <Label className="text-sm font-medium">Privacy</Label>
                  <p className="text-xs text-muted-foreground mb-3">Hide identifying details in the UI (display-only — stored data is unchanged)</p>
                  <div className="space-y-2">
                    <div
                      onClick={() => setEditForm(prev => ({ ...prev, mask_customer_names: !prev.mask_customer_names }))}
                      className={`flex items-center gap-3 p-3 border rounded-none cursor-pointer transition-colors ${editForm.mask_customer_names ? 'bg-primary/5 border-primary' : 'border-border hover:border-muted-foreground/50'}`}
                      data-testid="toggle-mask-customer-names"
                    >
                      <div className={`w-5 h-5 border rounded-sm flex items-center justify-center ${editForm.mask_customer_names ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                        {editForm.mask_customer_names && <Check className="h-3 w-3 text-primary-foreground" />}
                      </div>
                      <div>
                        <span className={`text-sm ${editForm.mask_customer_names ? 'font-medium' : 'text-muted-foreground'}`}>Mask customer names</span>
                        <p className="text-xs text-muted-foreground">Strips company names from filenames &amp; hides customer/property names (Intake Hub, Accounts, Viewer)</p>
                      </div>
                    </div>
                    <div
                      onClick={() => setEditForm(prev => ({ ...prev, mask_service_addresses: !prev.mask_service_addresses }))}
                      className={`flex items-center gap-3 p-3 border rounded-none cursor-pointer transition-colors ${editForm.mask_service_addresses ? 'bg-primary/5 border-primary' : 'border-border hover:border-muted-foreground/50'}`}
                      data-testid="toggle-mask-service-addresses"
                    >
                      <div className={`w-5 h-5 border rounded-sm flex items-center justify-center ${editForm.mask_service_addresses ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                        {editForm.mask_service_addresses && <Check className="h-3 w-3 text-primary-foreground" />}
                      </div>
                      <div>
                        <span className={`text-sm ${editForm.mask_service_addresses ? 'font-medium' : 'text-muted-foreground'}`}>Mask service addresses</span>
                        <p className="text-xs text-muted-foreground">Hides service/property addresses (Accounts, Demand report, Viewer)</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowEditAccount(false)}>
                  Cancel
                </Button>
                <Button className="rounded-none" onClick={handleEditAccount}>Save Permissions</Button>
              </div>
            </TabsContent>
            
            {/* Tab 3: Workflow Config (Module 2 — SuperAdmin only) */}
            <TabsContent value="workflow_config" className="mt-4">
              <WorkflowConfigTab
                accountId={selectedAccount?.id}
                accountName={selectedAccount?.organisation_name || selectedAccount?.company_name}
                onClose={() => setShowEditAccount(false)}
              />
            </TabsContent>
            
            {/* Tab 4: ACH / NACHA Originator Configuration */}
            <TabsContent value="ach" className="mt-4" data-testid="ach-config-panel">
              <div className="space-y-5">
                <div className="bg-blue-50 border border-blue-200 rounded-none p-3 text-xs text-blue-900 leading-relaxed">
                  These details populate the <strong>File Header</strong> (Immediate Destination, Immediate Origin, Company Name)
                  and <strong>Batch Header</strong> (Company ID, Originating DFI) of every NACHA file generated for this
                  organisation. The per-property ACH details (routing + account for each property LLC) are configured under
                  <em> UBM Onboarding → Properties → Update ACH</em>.
                </div>

                {/* Originator Company section */}
                <div className="space-y-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Originator Company
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Company Name *</Label>
                      <Input
                        className="rounded-none mt-1"
                        data-testid="nacha-company-name"
                        value={nachaOriginatorConfig.company_name}
                        onChange={(e) => setNachaOriginatorConfig({ ...nachaOriginatorConfig, company_name: e.target.value })}
                        placeholder="e.g. Allegiant Carter"
                        maxLength={23}
                      />
                      <p className="text-[10px] text-muted-foreground mt-1">Appears as Immediate Origin Name (pos 64-86) &amp; Batch Header Company Name (pos 5-20, first 16 chars).</p>
                    </div>
                    <div>
                      <Label>Company Federal Tax ID *</Label>
                      <Input
                        className="rounded-none mt-1 font-mono"
                        data-testid="nacha-company-tax-id"
                        value={nachaOriginatorConfig.company_tax_id}
                        onChange={(e) => setNachaOriginatorConfig({ ...nachaOriginatorConfig, company_tax_id: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                        placeholder="9 digits (e.g. 123456789)"
                        maxLength={9}
                      />
                      <p className="text-[10px] text-muted-foreground mt-1">Used to build Immediate Origin (&quot;1&quot; + Tax ID) &amp; Batch Header Company ID.</p>
                    </div>
                  </div>
                </div>

                {/* ODFI (Originator's Bank) section */}
                <div className="space-y-3 border-t border-border pt-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Originating Bank (ODFI) — where pulled funds are deposited
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Bank Name</Label>
                      <Input
                        className="rounded-none mt-1"
                        data-testid="nacha-odfi-bank-name"
                        value={nachaOriginatorConfig.odfi_bank_name}
                        onChange={(e) => setNachaOriginatorConfig({ ...nachaOriginatorConfig, odfi_bank_name: e.target.value })}
                        placeholder="e.g. Chase Boston"
                      />
                    </div>
                    <div>
                      <Label>Bank Account Name (alias) *</Label>
                      <Input
                        className="rounded-none mt-1"
                        data-testid="nacha-odfi-account-name"
                        value={nachaOriginatorConfig.odfi_account_name}
                        onChange={(e) => setNachaOriginatorConfig({ ...nachaOriginatorConfig, odfi_account_name: e.target.value })}
                        placeholder="e.g. AC-2"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Routing Number *</Label>
                      <Input
                        className="rounded-none mt-1 font-mono"
                        data-testid="nacha-odfi-routing"
                        value={nachaOriginatorConfig.odfi_routing_number}
                        onChange={(e) => setNachaOriginatorConfig({ ...nachaOriginatorConfig, odfi_routing_number: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                        placeholder="9 digits (e.g. 211318439)"
                        maxLength={9}
                      />
                    </div>
                    <div>
                      <Label>Account Number *</Label>
                      <Input
                        className="rounded-none mt-1 font-mono"
                        data-testid="nacha-odfi-account-number"
                        value={nachaOriginatorConfig.odfi_account_number}
                        onChange={(e) => setNachaOriginatorConfig({ ...nachaOriginatorConfig, odfi_account_number: e.target.value })}
                        placeholder="e.g. 294876212"
                      />
                    </div>
                  </div>
                </div>

                {/* Optional: Immediate Destination override */}
                <div className="space-y-3 border-t border-border pt-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Immediate Destination override <span className="normal-case font-normal text-muted-foreground">(optional — only if the file is routed through a correspondent bank different from the ODFI)</span>
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Destination Routing Number</Label>
                      <Input
                        className="rounded-none mt-1 font-mono"
                        data-testid="nacha-dest-routing"
                        value={nachaOriginatorConfig.immediate_destination_routing}
                        onChange={(e) => setNachaOriginatorConfig({ ...nachaOriginatorConfig, immediate_destination_routing: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                        placeholder="Defaults to ODFI routing"
                        maxLength={9}
                      />
                    </div>
                    <div>
                      <Label>Destination Bank Name</Label>
                      <Input
                        className="rounded-none mt-1"
                        data-testid="nacha-dest-name"
                        value={nachaOriginatorConfig.immediate_destination_name}
                        onChange={(e) => setNachaOriginatorConfig({ ...nachaOriginatorConfig, immediate_destination_name: e.target.value })}
                        placeholder="Defaults to ODFI bank name"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowEditAccount(false)}>
                  Cancel
                </Button>
                <Button
                  className="rounded-none"
                  onClick={handleSaveNachaConfig}
                  disabled={savingNachaConfig}
                  data-testid="save-nacha-config-btn"
                >
                  {savingNachaConfig ? 'Saving...' : 'Save ACH Configuration'}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Delete Organisation</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{selectedAccount?.organisation_name || selectedAccount?.company_name}&quot;? 
              This will permanently delete all associated documents, users, and data. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" className="rounded-none" onClick={handleDeleteAccount}>
              Delete Organisation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="rounded-none max-w-2xl max-h-[88vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b">
            <DialogTitle>Add User to Organisation</DialogTitle>
            <DialogDescription>Add an existing user or create a new one.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  className="rounded-none mt-1"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  placeholder="user@example.com"
                  required
                />
              </div>
              <div>
                <Label>Name (for new users)</Label>
                <Input
                  className="rounded-none mt-1"
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  placeholder="User name"
                />
              </div>
              <div>
                <Label>Password (for new users)</Label>
                <Input
                  type="password"
                  className="rounded-none mt-1"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder="Leave blank for existing users"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select
                  value={userForm.role}
                  onValueChange={(value) => setUserForm({ ...userForm, role: value })}
                >
                  <SelectTrigger className="rounded-none mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="px-6 py-3 border-t bg-background sticky bottom-0">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowAddUser(false)}>
                Cancel
              </Button>
              <Button type="submit" className="rounded-none">Add User</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="rounded-none max-w-2xl max-h-[88vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Create a user account and assign them to organizations
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0">
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium">Name *</Label>
                <Input
                  className="rounded-none mt-1"
                  value={createUserForm.name}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, name: e.target.value })}
                  placeholder="Full name"
                  required
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium">Email *</Label>
                <Input
                  type="email"
                  className="rounded-none mt-1"
                  value={createUserForm.email}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                  placeholder="user@example.com"
                  required
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium">Password *</Label>
                <Input
                  type="password"
                  className="rounded-none mt-1"
                  value={createUserForm.password}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                  placeholder="Minimum 6 characters"
                  minLength={6}
                  required
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium">Default Role</Label>
                <Select
                  value={createUserForm.role}
                  onValueChange={(value) => setCreateUserForm({ ...createUserForm, role: value })}
                >
                  <SelectTrigger className="rounded-none mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium">Default Home Page *</Label>
                <Select
                  value={createUserForm.default_page}
                  onValueChange={(value) => setCreateUserForm({ ...createUserForm, default_page: value })}
                >
                  <SelectTrigger className="rounded-none mt-1" data-testid="create-default-page-select">
                    <SelectValue placeholder="Select default page" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="/bills/dashboard">Bills Dashboard</SelectItem>
                    <SelectItem value="/bills">Bills List</SelectItem>
                    <SelectItem value="/payments/dashboard">Payments Dashboard</SelectItem>
                    <SelectItem value="/payments">Payment Files</SelectItem>
                    <SelectItem value="/ubm/bill2rollin">AI Bill2Rollin</SelectItem>
                    <SelectItem value="/ubm/properties">UBM Properties</SelectItem>
                    <SelectItem value="/cockpit">Cockpit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_super_admin"
                  checked={createUserForm.is_super_admin}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, is_super_admin: e.target.checked })}
                  className="rounded-none"
                />
                <Label htmlFor="is_super_admin" className="text-sm">Make Super Admin (can access all orgs)</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_super_pf_admin_create"
                  checked={createUserForm.is_super_pf_admin}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, is_super_pf_admin: e.target.checked })}
                  className="rounded-none"
                  data-testid="create-super-pf-admin-checkbox"
                />
                <Label htmlFor="is_super_pf_admin_create" className="text-sm">Super PF Admin (org-level PF access, no super admin space)</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_ops_admin_create"
                  checked={createUserForm.is_ops_admin}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, is_ops_admin: e.target.checked })}
                  className="rounded-none"
                  data-testid="create-ops-admin-checkbox"
                />
                <Label htmlFor="is_ops_admin_create" className="text-sm">Ops Admin (all orgs, manage issue flags)</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_ops_editor_create"
                  checked={createUserForm.is_ops_editor}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, is_ops_editor: e.target.checked })}
                  className="rounded-none"
                  data-testid="create-ops-editor-checkbox"
                />
                <Label htmlFor="is_ops_editor_create" className="text-sm">Ops Editor (all orgs, resolve issue flags)</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_ops_pf_create"
                  checked={createUserForm.is_ops_pf}
                  onChange={(e) => setCreateUserForm({
                    ...createUserForm,
                    is_ops_pf: e.target.checked,
                    default_page: e.target.checked ? '/payments/dashboard' : createUserForm.default_page,
                  })}
                  className="rounded-none"
                  data-testid="create-ops-pf-checkbox"
                />
                <Label htmlFor="is_ops_pf_create" className="text-sm">Ops PF (assigned orgs only · Super Admin &gt; Payments access · edits PF / Bills / Invoices)</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_finance_create"
                  checked={createUserForm.is_finance}
                  onChange={(e) => setCreateUserForm({
                    ...createUserForm,
                    is_finance: e.target.checked,
                    default_page: e.target.checked ? '/super-admin?tab=sa_finance_report' : createUserForm.default_page,
                  })}
                  className="rounded-none"
                  data-testid="create-finance-checkbox"
                />
                <Label htmlFor="is_finance_create" className="text-sm">Finance (Subscriptions menu + Finance Credit/Debit report &amp; Refunds only)</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_support_create"
                  checked={createUserForm.is_support}
                  onChange={(e) => setCreateUserForm({
                    ...createUserForm,
                    is_support: e.target.checked,
                    default_page: e.target.checked ? '/super-admin?tab=support-requests' : createUserForm.default_page,
                  })}
                  className="rounded-none"
                  data-testid="create-support-checkbox"
                />
                <Label htmlFor="is_support_create" className="text-sm">Support Team (cross-org access · Super Admin Dashboard + Support Requests only)</Label>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">Assign to Organizations</Label>
                <div className="border border-border rounded-none p-3 max-h-48 overflow-y-auto space-y-2">
                  {accounts.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No organizations available</p>
                  ) : (
                    accounts.map((account) => (
                      <div key={account.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`create-org-${account.id}`}
                          checked={createUserForm.assigned_accounts.includes(account.id)}
                          onChange={() => toggleAccountAssignment(account.id, setCreateUserForm, createUserForm.assigned_accounts)}
                          className="rounded-none"
                        />
                        <Label htmlFor={`create-org-${account.id}`} className="text-sm cursor-pointer">
                          {account.organisation_name || account.company_name}
                        </Label>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {createUserForm.assigned_accounts.length === 0 
                    ? 'User will not be assigned to any organization yet'
                    : `Selected: ${createUserForm.assigned_accounts.length} org(s)`}
                </p>
              </div>
            </div>
            <DialogFooter className="px-6 py-3 border-t bg-background shrink-0">
              <Button 
                type="button" 
                variant="outline" 
                className="rounded-none" 
                onClick={() => {
                  setShowCreateUser(false);
                  setCreateUserForm({
                    name: '',
                    email: '',
                    password: '',
                    role: 'viewer',
                    is_super_admin: false,
                    assigned_accounts: [],
                  });
                }}
              >
                Cancel
              </Button>
              <Button type="submit" className="rounded-none" disabled={creatingUser}>
                {creatingUser ? 'Creating...' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User / Manage User Dialog */}
      <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
        <DialogContent className="rounded-none max-w-2xl max-h-[88vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
            <DialogTitle>Edit User: {editingUser?.name}</DialogTitle>
            <DialogDescription>
              {editingUser?.email} • Update profile and organization assignments
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUserAssignments} className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0">
              {/* Name Field */}
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium mb-1 block">Name</Label>
                <Input
                  className="rounded-none"
                  value={editUserForm.name}
                  onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })}
                  placeholder="Full Name"
                />
              </div>
              
              {/* Super Admin Checkbox */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-is-super-admin"
                  checked={editUserForm.is_super_admin}
                  onChange={(e) => setEditUserForm({ ...editUserForm, is_super_admin: e.target.checked })}
                  className="rounded-none"
                />
                <Label htmlFor="edit-is-super-admin" className="text-sm cursor-pointer">
                  Super Admin (Full platform access)
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-is-super-pf-admin"
                  checked={editUserForm.is_super_pf_admin}
                  onChange={(e) => setEditUserForm({ ...editUserForm, is_super_pf_admin: e.target.checked })}
                  className="rounded-none"
                  data-testid="edit-super-pf-admin-checkbox"
                />
                <Label htmlFor="edit-is-super-pf-admin" className="text-sm cursor-pointer">
                  Super PF Admin (org-level PF access, no super admin space)
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-is-ops-admin"
                  checked={editUserForm.is_ops_admin}
                  onChange={(e) => setEditUserForm({ ...editUserForm, is_ops_admin: e.target.checked })}
                  className="rounded-none"
                  data-testid="edit-ops-admin-checkbox"
                />
                <Label htmlFor="edit-is-ops-admin" className="text-sm cursor-pointer">
                  Ops Admin (all orgs, manage issue flags)
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-is-ops-editor"
                  checked={editUserForm.is_ops_editor}
                  onChange={(e) => setEditUserForm({ ...editUserForm, is_ops_editor: e.target.checked })}
                  className="rounded-none"
                  data-testid="edit-ops-editor-checkbox"
                />
                <Label htmlFor="edit-is-ops-editor" className="text-sm cursor-pointer">
                  Ops Editor (all orgs, resolve issue flags)
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-is-ops-pf"
                  checked={editUserForm.is_ops_pf}
                  onChange={(e) => setEditUserForm({
                    ...editUserForm,
                    is_ops_pf: e.target.checked,
                    default_page: e.target.checked ? '/payments/dashboard' : editUserForm.default_page,
                  })}
                  className="rounded-none"
                  data-testid="edit-ops-pf-checkbox"
                />
                <Label htmlFor="edit-is-ops-pf" className="text-sm cursor-pointer">
                  Ops PF (assigned orgs only · Super Admin &gt; Payments access · edits PF / Bills / Invoices)
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-is-finance"
                  checked={editUserForm.is_finance}
                  onChange={(e) => setEditUserForm({
                    ...editUserForm,
                    is_finance: e.target.checked,
                    default_page: e.target.checked ? '/super-admin?tab=sa_finance_report' : editUserForm.default_page,
                  })}
                  className="rounded-none"
                  data-testid="edit-finance-checkbox"
                />
                <Label htmlFor="edit-is-finance" className="text-sm cursor-pointer">
                  Finance (Subscriptions menu + Finance Credit/Debit report &amp; Refunds only)
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-is-support"
                  checked={editUserForm.is_support}
                  onChange={(e) => setEditUserForm({
                    ...editUserForm,
                    is_support: e.target.checked,
                    default_page: e.target.checked ? '/super-admin?tab=support-requests' : editUserForm.default_page,
                  })}
                  className="rounded-none"
                  data-testid="edit-support-checkbox"
                />
                <Label htmlFor="edit-is-support" className="text-sm cursor-pointer">
                  Support Team (cross-org access · Super Admin Dashboard + Support Requests only)
                </Label>
              </div>
              
              {/* Default Role for New Assignments */}
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium mb-1 block">Default Role for New Orgs</Label>
                <Select
                  value={editUserForm.default_role}
                  onValueChange={(v) => setEditUserForm({ ...editUserForm, default_role: v })}
                >
                  <SelectTrigger className="rounded-none">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Default Home Page */}
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium mb-1 block">Default Home Page</Label>
                <Select
                  value={editUserForm.default_page || '/bills/dashboard'}
                  onValueChange={(v) => setEditUserForm({ ...editUserForm, default_page: v })}
                >
                  <SelectTrigger className="rounded-none" data-testid="edit-default-page-select">
                    <SelectValue placeholder="Select default page" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="/bills/dashboard">Bills Dashboard</SelectItem>
                    <SelectItem value="/bills">Bills List</SelectItem>
                    <SelectItem value="/payments/dashboard">Payments Dashboard</SelectItem>
                    <SelectItem value="/payments">Payment Files</SelectItem>
                    <SelectItem value="/ubm/bill2rollin">AI Bill2Rollin</SelectItem>
                    <SelectItem value="/ubm/properties">UBM Properties</SelectItem>
                    <SelectItem value="/cockpit">Cockpit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Organization Assignments with Role Selection */}
              <div>
                <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">Organization Assignments</Label>
                <div className="border border-border rounded-none p-3 max-h-64 overflow-y-auto space-y-2">
                  {accounts.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No organizations available</p>
                  ) : (
                    accounts.map((account) => (
                      <div key={account.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`edit-org-${account.id}`}
                          checked={editUserForm.assigned_accounts.includes(account.id)}
                          onChange={() => toggleAccountAssignment(account.id, setEditUserForm, editUserForm.assigned_accounts)}
                          className="rounded-none"
                        />
                        <Label htmlFor={`edit-org-${account.id}`} className="text-sm cursor-pointer flex-1">
                          {account.organisation_name || account.company_name}
                        </Label>
                        {editUserForm.assigned_accounts.includes(account.id) && (
                          <Select
                            value={getAccountRole(account.id)}
                            onValueChange={(v) => updateAccountRole(account.id, v)}
                          >
                            <SelectTrigger className="rounded-none w-24 h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-none">
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="editor">Editor</SelectItem>
                              <SelectItem value="viewer">Viewer</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {editUserForm.assigned_accounts.length === 0 
                    ? 'User will not have access to any organization'
                    : `Assigned to ${editUserForm.assigned_accounts.length} organization(s)`}
                </p>
              </div>
            </div>
            <DialogFooter className="px-6 py-3 border-t bg-background shrink-0">
              <Button 
                type="button" 
                variant="outline" 
                className="rounded-none" 
                onClick={() => {
                  setShowEditUser(false);
                  setEditingUser(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" className="rounded-none" disabled={updatingUser}>
                {updatingUser ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vendor Modal */}
      <Dialog open={showVendorModal} onOpenChange={setShowVendorModal}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingVendor ? 'Edit Vendor' : 'Add New Vendor'}</DialogTitle>
            <DialogDescription>
              {editingVendor ? 'Update vendor details' : 'Add a new utility vendor to the global registry'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveVendor}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <Label>Vendor Name *</Label>
                <Input
                  className="rounded-none mt-1"
                  value={vendorForm.vendor_name}
                  onChange={(e) => setVendorForm({ ...vendorForm, vendor_name: e.target.value })}
                  placeholder="e.g., Pacific Gas & Electric"
                  required
                />
              </div>
              <div>
                <Label>Vendor Code</Label>
                <Input
                  className="rounded-none mt-1 font-mono"
                  value={vendorForm.vendor_code}
                  onChange={(e) => setVendorForm({ ...vendorForm, vendor_code: e.target.value.toUpperCase() })}
                  placeholder="e.g., PGE"
                />
              </div>
              <div>
                <Label>Support Email</Label>
                <Input
                  type="email"
                  className="rounded-none mt-1"
                  value={vendorForm.email}
                  onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })}
                  placeholder="support@vendor.com"
                />
              </div>
              <div>
                <Label>Support Phone</Label>
                <Input
                  className="rounded-none mt-1"
                  value={vendorForm.support_phone}
                  onChange={(e) => setVendorForm({ ...vendorForm, support_phone: e.target.value })}
                  placeholder="1-800-555-0100"
                />
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  className="rounded-none mt-1"
                  value={vendorForm.website}
                  onChange={(e) => setVendorForm({ ...vendorForm, website: e.target.value })}
                  placeholder="https://www.vendor.com"
                />
              </div>
              <div className="col-span-2">
                <Label>Portal URL</Label>
                <Input
                  className="rounded-none mt-1"
                  value={vendorForm.portal_url}
                  onChange={(e) => setVendorForm({ ...vendorForm, portal_url: e.target.value })}
                  placeholder="https://portal.vendor.com"
                />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Input
                  className="rounded-none mt-1"
                  value={vendorForm.address}
                  onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })}
                  placeholder="123 Main Street"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  className="rounded-none mt-1"
                  value={vendorForm.city}
                  onChange={(e) => setVendorForm({ ...vendorForm, city: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>State</Label>
                  <Input
                    className="rounded-none mt-1"
                    value={vendorForm.state}
                    onChange={(e) => setVendorForm({ ...vendorForm, state: e.target.value.toUpperCase().slice(0, 2) })}
                    maxLength={2}
                  />
                </div>
                <div>
                  <Label>ZIP</Label>
                  <Input
                    className="rounded-none mt-1"
                    value={vendorForm.zip}
                    onChange={(e) => setVendorForm({ ...vendorForm, zip: e.target.value })}
                  />
                </div>
              </div>

              {/* Commodities */}
              <div className="col-span-2">
                <Label className="mb-2 block">Commodities Served</Label>
                <div className="flex flex-wrap gap-2">
                  {['electricity', 'gas', 'water', 'ev_charger', 'solar', 'steam', 'chilled_water'].map(c => (
                    <Button
                      key={c}
                      type="button"
                      variant={vendorForm.commodities_served?.includes(c) ? 'default' : 'outline'}
                      size="sm"
                      className="rounded-none capitalize"
                      onClick={() => toggleCommodity(c)}
                    >
                      {c.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Payment Methods */}
              <div className="col-span-2">
                <Label className="mb-2 block">Accepted Payment Methods</Label>
                <div className="flex flex-wrap gap-2">
                  {['ach', 'check', 'credit_card', 'wire', 'account_pay', 'auto_pay'].map(m => (
                    <Button
                      key={m}
                      type="button"
                      variant={vendorForm.payment_methods?.[m]?.enabled ? 'default' : 'outline'}
                      size="sm"
                      className="rounded-none uppercase"
                      onClick={() => togglePaymentMethod(m)}
                    >
                      {m.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-none" onClick={() => { setShowVendorModal(false); resetVendorForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="rounded-none">
                {editingVendor ? 'Save Changes' : 'Add Vendor'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Vendor Confirmation */}
      <Dialog open={showDeleteVendor} onOpenChange={setShowDeleteVendor}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Delete Vendor</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{editingVendor?.vendor_name}"? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowDeleteVendor(false)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" className="rounded-none" onClick={handleDeleteVendor}>
              Delete Vendor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Vendor Modal */}
      <Dialog open={showMergeModal} onOpenChange={setShowMergeModal}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle>Merge Vendor</DialogTitle>
            <DialogDescription>
              Merge "{mergeVendor?.vendor_name}" into an existing vendor. All linked accounts will be transferred.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Select Target Vendor</Label>
            <Select onValueChange={(vendorId) => handleReviewAction(mergeVendor?.id, 'merge', vendorId)}>
              <SelectTrigger className="rounded-none mt-2">
                <SelectValue placeholder="Select vendor to merge into" />
              </SelectTrigger>
              <SelectContent className="rounded-none max-h-60">
                {vendors
                  .filter(v => v.id !== mergeVendor?.id && v.review_status !== 'for_review')
                  .map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.vendor_name} ({v.vendor_code || 'No code'})
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none" onClick={() => { setShowMergeModal(false); setMergeVendor(null); }}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PostgreSQL Connection Error Dialog */}
      <Dialog open={showPgErrorDialog} onOpenChange={setShowPgErrorDialog}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="h-5 w-5" />
              BP Postgres Connection Details
            </DialogTitle>
            <DialogDescription>
              External PostgreSQL database connection status
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Connection Info */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Host:</span>
                <span className="font-mono">{systemHealth?.external_postgres?.host || 'Not configured'}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Database:</span>
                <span className="font-mono">{systemHealth?.external_postgres?.database || 'Not configured'}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status:</span>
                {systemHealth?.external_postgres?.status === 'connected' ? (
                  <Badge variant="default" className="rounded-none bg-green-600">Connected</Badge>
                ) : systemHealth?.external_postgres?.status === 'not_configured' ? (
                  <Badge variant="secondary" className="rounded-none">Not Configured</Badge>
                ) : (
                  <Badge variant="destructive" className="rounded-none">Disconnected</Badge>
                )}
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">App Outbound IP:</span>
                <span className="font-mono">{systemHealth?.outbound_ip || 'Unknown'}</span>
              </div>
            </div>
            
            {/* Error Message */}
            {systemHealth?.external_postgres?.error && (
              <div className="bg-destructive/10 border border-destructive/20 p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-destructive text-sm">Connection Error</p>
                    <p className="text-sm mt-1">{systemHealth.external_postgres.error}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Whitelisting Instructions */}
            {systemHealth?.external_postgres?.status === 'disconnected' && systemHealth?.outbound_ip && (
              <div className="bg-muted/50 border border-border p-4">
                <p className="font-semibold text-sm mb-2">Whitelist Instructions</p>
                <p className="text-xs text-muted-foreground mb-2">
                  Add this line to your PostgreSQL <code className="bg-muted px-1 rounded">pg_hba.conf</code>:
                </p>
                <pre className="text-xs font-mono bg-muted p-2 overflow-x-auto">
                  host    {systemHealth?.external_postgres?.database || 'bpanalytics'}    all    {systemHealth.outbound_ip}/32    md5
                </pre>
                <p className="text-xs text-muted-foreground mt-2">
                  Then reload PostgreSQL: <code className="bg-muted px-1 rounded">sudo systemctl reload postgresql</code>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              className="rounded-none" 
              onClick={() => {
                fetchSystemHealth();
                toast.info('Refreshing connection status...');
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry Connection
            </Button>
            <Button className="rounded-none" onClick={() => setShowPgErrorDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Customer Config Dialog */}
      <Dialog open={showAddCustomerConfig} onOpenChange={setShowAddCustomerConfig}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle>Add Engine Customer Configuration</DialogTitle>
            <DialogDescription>
              Add a customer ID to pull payment files from BillPortal Engine. Optionally map to an organization for auto-assignment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Customer Name</Label>
              <Input
                className="rounded-none mt-1"
                placeholder="e.g., Allegiant Carter Management"
                value={newCustomerConfig.customer_name}
                onChange={(e) => setNewCustomerConfig({ ...newCustomerConfig, customer_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Customer ID (UUID)</Label>
              <Input
                className="rounded-none mt-1 font-mono"
                placeholder="e.g., 4cfc45b2-1f76-4c5d-a31b-68a1d4afe50c"
                value={newCustomerConfig.customer_id}
                onChange={(e) => setNewCustomerConfig({ ...newCustomerConfig, customer_id: e.target.value })}
              />
            </div>
            <div>
              <Label>Auto-Assign to Organization (Optional)</Label>
              <select
                className="w-full mt-1 border rounded-none px-3 py-2 text-sm bg-background"
                value={newCustomerConfig.auto_assign_account_id}
                onChange={(e) => {
                  const selectedAccount = accounts.find(a => a.id === e.target.value);
                  setNewCustomerConfig({
                    ...newCustomerConfig,
                    auto_assign_account_id: e.target.value,
                    auto_assign_account_name: selectedAccount?.organisation_name || ''
                  });
                }}
                data-testid="auto-assign-account-select"
              >
                <option value="">-- None (Manual Assignment) --</option>
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.organisation_name}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                When set, files pulled for this customer will be auto-assigned to the selected organization.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowAddCustomerConfig(false)}>
              Cancel
            </Button>
            <Button className="rounded-none" onClick={handleAddCustomerConfig}>
              Add Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign File Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Payment File</DialogTitle>
            <DialogDescription>
              Assign this payment file to an organization
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {assigningFile && (
              <div className="bg-muted p-3 rounded-none">
                <p className="font-medium">{assigningFile.batch_name}</p>
                <p className="text-sm text-muted-foreground">Engine Status: {assigningFile.engine_status}</p>
              </div>
            )}
            <div>
              <Label>Select Organization</Label>
              <Select 
                value={assigningFile?.selectedAccountId || ''} 
                onValueChange={(v) => setAssigningFile({ ...assigningFile, selectedAccountId: v })}
              >
                <SelectTrigger className="rounded-none mt-1">
                  <SelectValue placeholder="Choose organization..." />
                </SelectTrigger>
                <SelectContent>
                  {accounts.filter(a => a.enabled_apps?.payments).map(acc => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.organisation_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Only organizations with Payments enabled are shown</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button className="rounded-none" onClick={handleAssignFile}>
              Assign File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Files Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="rounded-none max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Preview Files from Engine</DialogTitle>
            <DialogDescription>
              Files available for customer {previewCustomerId}
              {pfePreviewFiles.length > 0 && (
                <span className="ml-2">
                  — <span className="text-green-600 font-medium">{pfePreviewFiles.filter(f => !f.exists).length} new</span>
                  {pfePreviewFiles.filter(f => f.exists).length > 0 && (
                    <>, <span className="text-muted-foreground">{pfePreviewFiles.filter(f => f.exists).length} already pulled</span></>
                  )}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 max-h-96 overflow-y-auto">
            {pfePreviewFiles.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No files found for this customer ID</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">Batch Name</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Generated</th>
                    <th className="px-3 py-2 text-center">Platform</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {pfePreviewFiles.map(f => (
                    <tr key={f.paymentFileId} className={f.exists ? 'bg-muted/30' : ''}>
                      <td className="px-3 py-2 font-medium">{f.batchName}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="rounded-none">{f.paymentFileStatus}</Badge>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {new Date(f.generatedAt).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {f.exists ? (
                          <Badge className="rounded-none bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200" data-testid={`preview-exists-${f.paymentFileId}`}>
                            Exists
                          </Badge>
                        ) : (
                          <Badge className="rounded-none bg-green-100 text-green-700 hover:bg-green-100 border-green-200" data-testid={`preview-new-${f.paymentFileId}`}>
                            New
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {f.exists ? (
                          <span className="text-xs text-muted-foreground">Already pulled</span>
                        ) : (
                          <Button
                            size="sm"
                            className="rounded-none h-7 px-3"
                            disabled={pfePullingSingle === f.paymentFileId}
                            onClick={() => handlePullSingleFile(f)}
                            data-testid={`preview-pull-${f.paymentFileId}`}
                          >
                            {pfePullingSingle === f.paymentFileId ? (
                              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <Download className="h-3 w-3 mr-1" />
                            )}
                            Pull
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdmin;
